/**
 * Verticaly Studio: Banner Lab v4
 * Scroll-based multi-banner creation tool
 * Version 4.2.0
 *
 * Features:
 * - Scroll layout: Each banner row has Mockup + Canvas + Controls
 * - BGM integration
 * - Bulk download all banners and mockups
 * - html2canvas mockup capture with full UI elements
 */

(function() {
  'use strict';

  // Exit if app doesn't exist
  const app = document.getElementById('bannerLabApp');
  if (!app) return;

  // ============================================
  // Configuration
  // ============================================
  const DEBUG = false; // Set to true for development logging

  // ============================================
  // Layout Definitions
  // ============================================
  // Use exact fractions for precise positioning (1/3 = 0.3333..., 2/3 = 0.6666...)
  const ONE_THIRD = 1 / 3;
  const TWO_THIRDS = 2 / 3;

  const LAYOUTS = {
    1: [
      { id: 'single', name: 'Single', type: 'single', panels: [{ x: 0, y: 0, w: 1, h: 1 }] }
    ],
    2: [
      { id: 'h-equal', name: 'H Split', type: 'horizontal', panels: [{ x: 0, y: 0, w: 0.5, h: 1 }, { x: 0.5, y: 0, w: 0.5, h: 1 }] },
      { id: 'v-equal', name: 'V Split', type: 'vertical', panels: [{ x: 0, y: 0, w: 1, h: 0.5 }, { x: 0, y: 0.5, w: 1, h: 0.5 }] },
      { id: 'h-left', name: 'Left Big', type: 'horizontal', panels: [{ x: 0, y: 0, w: TWO_THIRDS, h: 1 }, { x: TWO_THIRDS, y: 0, w: ONE_THIRD, h: 1 }] },
      { id: 'h-right', name: 'Right Big', type: 'horizontal', panels: [{ x: 0, y: 0, w: ONE_THIRD, h: 1 }, { x: ONE_THIRD, y: 0, w: TWO_THIRDS, h: 1 }] },
    ],
    3: [
      { id: '3h-equal', name: 'H Thirds', type: 'horizontal', panels: [{ x: 0, y: 0, w: ONE_THIRD, h: 1 }, { x: ONE_THIRD, y: 0, w: ONE_THIRD, h: 1 }, { x: TWO_THIRDS, y: 0, w: ONE_THIRD, h: 1 }] },
      { id: '3-left-big', name: 'Left + 2', type: 'mixed', panels: [{ x: 0, y: 0, w: 0.6, h: 1 }, { x: 0.6, y: 0, w: 0.4, h: 0.5 }, { x: 0.6, y: 0.5, w: 0.4, h: 0.5 }] },
      { id: '3-right-big', name: 'Right + 2', type: 'mixed', panels: [{ x: 0, y: 0, w: 0.4, h: 0.5 }, { x: 0, y: 0.5, w: 0.4, h: 0.5 }, { x: 0.4, y: 0, w: 0.6, h: 1 }] },
      { id: '3-top-big', name: 'Top + 2', type: 'mixed', panels: [{ x: 0, y: 0, w: 1, h: 0.6 }, { x: 0, y: 0.6, w: 0.5, h: 0.4 }, { x: 0.5, y: 0.6, w: 0.5, h: 0.4 }] },
    ],
    4: [
      { id: '4-grid', name: '2x2 Grid', type: 'grid', panels: [{ x: 0, y: 0, w: 0.5, h: 0.5 }, { x: 0.5, y: 0, w: 0.5, h: 0.5 }, { x: 0, y: 0.5, w: 0.5, h: 0.5 }, { x: 0.5, y: 0.5, w: 0.5, h: 0.5 }] },
      { id: '4-left-big', name: 'Left + 3', type: 'mixed', panels: [{ x: 0, y: 0, w: 0.6, h: 1 }, { x: 0.6, y: 0, w: 0.4, h: ONE_THIRD }, { x: 0.6, y: ONE_THIRD, w: 0.4, h: ONE_THIRD }, { x: 0.6, y: TWO_THIRDS, w: 0.4, h: ONE_THIRD }] },
      { id: '4-right-big', name: 'Right + 3', type: 'mixed', panels: [{ x: 0, y: 0, w: 0.4, h: ONE_THIRD }, { x: 0, y: ONE_THIRD, w: 0.4, h: ONE_THIRD }, { x: 0, y: TWO_THIRDS, w: 0.4, h: ONE_THIRD }, { x: 0.4, y: 0, w: 0.6, h: 1 }] },
      { id: '4-t-shape', name: 'T Shape', type: 'mixed', panels: [{ x: 0, y: 0, w: ONE_THIRD, h: 0.5 }, { x: ONE_THIRD, y: 0, w: ONE_THIRD, h: 0.5 }, { x: TWO_THIRDS, y: 0, w: ONE_THIRD, h: 0.5 }, { x: 0, y: 0.5, w: 1, h: 0.5 }] },
    ]
  };

  // ============================================
  // State
  // ============================================

  // Check WebP canvas export support (Safari doesn't support it)
  const supportsWebPExport = (() => {
    const testCanvas = document.createElement('canvas');
    testCanvas.width = 1;
    testCanvas.height = 1;
    const testUrl = testCanvas.toDataURL('image/webp');
    const supported = testUrl.startsWith('data:image/webp');
    if (!supported) {
      console.warn('[BannerLab] WebP canvas export not supported in this browser (Safari/iOS). Will use PNG as fallback.');
    }
    return supported;
  })();

  const state = {
    banners: [],  // Array of banner objects
    activeBannerIndex: 0,
    currentTab: 'static',
    bgmPlaying: false,
    locale: app.dataset.locale || 'en',
    supportsWebP: supportsWebPExport
  };

  // Debounce timers for mockup preview updates (per banner)
  const mockupPreviewTimers = {};

  // Intersection Observer for video playback control
  let bannerRowObserver = null;

  // Default banner state
  function createDefaultBanner() {
    return {
      outputWidth: 1080,
      outputHeight: 1920,
      panelCount: 1,
      selectedLayout: JSON.parse(JSON.stringify(LAYOUTS[1][0])), // Deep copy
      format: 'png',
      panels: [{ image: null, x: 0, y: 0, scale: 1, naturalWidth: 0, naturalHeight: 0 }],
      currentPlatform: 'instagram',
      currentDevice: 'apple',
      aspectRatio: '9:16',
      // Grid line settings
      gridLineEnabled: false,
      gridLineColor: '#ffffff',
      gridLineWidth: 2,
      gridLineOffsets: {}, // Custom offsets for grid lines { 'v-0.5': 0.52, 'h-0.33': 0.35 }
      // Texture overlay (digital, film, paper, or null)
      textureType: null,
      textureIntensity: 0.5  // 0.1 - 1.0 (enhanced range)
    };
  }

  // ============================================
  // Login Check for Downloads
  // ============================================
  function isUserLoggedIn() {
    return window.VerticalyStudio && window.VerticalyStudio.isLoggedIn === true;
  }

  function showLoginRequiredModal() {
    const isJapanese = state.locale === 'ja';

    // Check if modal already exists
    let modal = document.getElementById('labAuthModal');
    if (modal) {
      modal.classList.add('active');
      return;
    }

    // Create Authentication Gate Modal
    const modalHTML = `
      <div class="lab-auth-modal" id="labAuthModal">
        <div class="lab-auth-modal__content">
          <button class="lab-auth-modal__close" id="labAuthClose">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          <div class="lab-auth-modal__icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <circle cx="12" cy="12" r="10"/>
              <circle cx="12" cy="12" r="3"/>
              <path d="M12 5v2M12 17v2M5 12h2M17 12h2"/>
            </svg>
          </div>
          <h3 class="lab-auth-modal__title">AUTHENTICATION REQUIRED</h3>
          <p class="lab-auth-modal__desc">
            ${isJapanese
              ? 'ダウンロードにはVerticalyアカウントへのログインが必要です。'
              : 'Please log in to your Verticaly account to download.'}
          </p>
          <a href="${window.VerticalyStudio?.loginUrl || '/account/login'}" class="lab-auth-modal__btn">
            ${isJapanese ? 'ログイン' : 'LOG IN'}
          </a>
          <a href="${window.VerticalyStudio?.registerUrl || '/account/register'}" class="lab-auth-modal__btn lab-auth-modal__btn--secondary">
            ${isJapanese ? '新規登録' : 'REGISTER'}
          </a>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHTML);

    modal = document.getElementById('labAuthModal');
    const closeBtn = document.getElementById('labAuthClose');

    // Show modal
    requestAnimationFrame(() => modal.classList.add('active'));

    // Close handlers
    const closeModal = () => modal.classList.remove('active');

    closeBtn.addEventListener('click', closeModal);

    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });

    // ESC key to close
    const escHandler = (e) => {
      if (e.key === 'Escape' && modal.classList.contains('active')) {
        closeModal();
      }
    };
    document.addEventListener('keydown', escHandler);
  }

  // ============================================
  // Video Playback Control with Intersection Observer
  // ============================================
  function setupBannerRowObserver() {
    // Disconnect existing observer if any
    if (bannerRowObserver) {
      bannerRowObserver.disconnect();
    }

    // Create new observer
    bannerRowObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const row = entry.target;
        const videos = row.querySelectorAll('.v0-banner-creator__panel-video');

        if (entry.isIntersecting) {
          // Banner row is visible - play videos
          videos.forEach(video => {
            if (video.paused) {
              video.play().catch(() => {});
            }
          });
        } else {
          // Banner row is not visible - pause videos
          videos.forEach(video => {
            if (!video.paused) {
              video.pause();
            }
          });
        }
      });
    }, {
      root: document.getElementById('bannerScrollContainer'),
      rootMargin: '50px 0px', // Start loading slightly before visible
      threshold: 0.1 // Trigger when 10% visible
    });
  }

  function observeBannerRows() {
    const rows = document.querySelectorAll('.banner-lab-v4__banner-row');
    rows.forEach(row => {
      bannerRowObserver.observe(row);
    });
  }

  // ============================================
  // Initialize
  // ============================================
  function init() {
    // Start with 5 banners (fixed, no add button needed)
    for (let i = 0; i < 5; i++) {
      state.banners.push(createDefaultBanner());
    }

    bindHeaderEvents();
    bindBannerRowEvents();
    setupBannerRowObserver();
    renderAllBannerRows();

    // Initialize micro-interactions
    initRippleEffects();
  }

  // ============================================
  // Ripple Effect (Click Burst)
  // ============================================
  function initRippleEffects() {
    document.addEventListener('click', (e) => {
      const target = e.target.closest('.banner-lab-v4__tab, .v0-frame-capture__video-dropzone-btn, .v0-frame-capture__filter-btn, .v0-frame-capture__btn, .v0-banner-creator__btn');
      if (!target) return;

      const rect = target.getBoundingClientRect();
      const ripple = document.createElement('span');
      ripple.className = 'lab-ripple';

      const size = Math.max(rect.width, rect.height);
      ripple.style.width = ripple.style.height = size + 'px';
      ripple.style.left = (e.clientX - rect.left - size / 2) + 'px';
      ripple.style.top = (e.clientY - rect.top - size / 2) + 'px';

      target.appendChild(ripple);

      ripple.addEventListener('animationend', () => ripple.remove());
    });
  }

  // ============================================
  // Header Events
  // ============================================
  function bindHeaderEvents() {
    // Tab switching
    const tabs = document.querySelectorAll('.banner-lab-v4__tab');
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        tabs.forEach(t => t.classList.remove('banner-lab-v4__tab--active'));
        tab.classList.add('banner-lab-v4__tab--active');
        state.currentTab = tab.dataset.tab;

        const captureContent = document.getElementById('captureContent');
        const bannerContent = document.getElementById('bannerContent');

        if (captureContent && bannerContent) {
          if (state.currentTab === 'capture') {
            captureContent.style.display = 'block';
            bannerContent.style.display = 'none';
          } else {
            captureContent.style.display = 'none';
            bannerContent.style.display = 'block';
          }
        }
      });
    });

    // BGM
    const bgmBtn = document.getElementById('bgmBtn');
    if (bgmBtn) {
      bgmBtn.addEventListener('click', toggleBGM);
    }

    // Add Banner
    const addBtn = document.getElementById('addBannerBtn');
    if (addBtn) {
      addBtn.addEventListener('click', addBanner);
    }

    // Bulk Download
    const bulkBtn = document.getElementById('bulkDownloadBtn');
    if (bulkBtn) {
      bulkBtn.addEventListener('click', bulkDownloadAll);
    }
  }

  // ============================================
  // BGM Toggle
  // ============================================
  function toggleBGM() {
    state.bgmPlaying = !state.bgmPlaying;
    const btn = document.getElementById('bgmBtn');

    if (state.bgmPlaying) {
      btn.classList.add('active');
      // TODO: Connect to Vertical Studio BGM system
    } else {
      btn.classList.remove('active');
    }
  }

  // ============================================
  // Add Banner
  // ============================================
  function addBanner() {
    if (state.banners.length >= 10) {
      alert(state.locale === 'ja' ? '最大10個まで作成できます' : 'Maximum 10 banners');
      return;
    }

    state.banners.push(createDefaultBanner());
    renderAllBannerRows();

    // Scroll to new banner
    setTimeout(() => {
      const container = document.getElementById('bannerScrollContainer');
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      });
    }, 100);
  }

  // ============================================
  // Delete Banner
  // ============================================
  function deleteBanner(index) {
    if (state.banners.length <= 1) return;

    state.banners.splice(index, 1);
    if (state.activeBannerIndex >= state.banners.length) {
      state.activeBannerIndex = state.banners.length - 1;
    }
    renderAllBannerRows();
  }

  // ============================================
  // Render All Banner Rows
  // ============================================
  function renderAllBannerRows() {
    const container = document.getElementById('bannerScrollContainer');
    container.innerHTML = '';

    state.banners.forEach((banner, index) => {
      const row = createBannerRow(banner, index);
      container.appendChild(row);
    });

    // Re-observe all banner rows for video playback control
    if (bannerRowObserver) {
      observeBannerRows();
    }
  }

  // ============================================
  // Create Banner Row
  // ============================================
  function createBannerRow(banner, index) {
    const row = document.createElement('div');
    row.className = 'banner-lab-v4__banner-row' + (index === state.activeBannerIndex ? ' active' : '');
    row.dataset.bannerIndex = index;

    // ===== Mockup Section =====
    const mockupSection = document.createElement('div');
    mockupSection.className = 'banner-lab-v4__mockup-section';
    mockupSection.innerHTML = `
      <div class="banner-lab-v4__mockup-header">
        <div class="mockup-platform-tabs">
          <button type="button" class="mockup-tab mockup-tab--active" data-platform="instagram" data-banner-index="${index}" title="Instagram">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="2" y="2" width="20" height="20" rx="5"/>
              <circle cx="12" cy="12" r="4"/>
              <circle cx="18" cy="6" r="1.5" fill="currentColor"/>
            </svg>
          </button>
          <button type="button" class="mockup-tab" data-platform="tiktok" data-banner-index="${index}" title="TikTok">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
            </svg>
          </button>
        </div>
        <div class="mockup-device-tabs">
          <button type="button" class="device-tab device-tab--active" data-device="apple" data-banner-index="${index}" title="iPhone">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
          </button>
          <button type="button" class="device-tab" data-device="google" data-banner-index="${index}" title="Pixel">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          </button>
          <button type="button" class="device-tab" data-device="samsung" data-banner-index="${index}" title="Galaxy">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><ellipse cx="12" cy="12" rx="10" ry="4" stroke="currentColor" stroke-width="1.5" fill="none"/><ellipse cx="12" cy="12" rx="10" ry="4" transform="rotate(60 12 12)" stroke="currentColor" stroke-width="1.5" fill="none"/><ellipse cx="12" cy="12" rx="10" ry="4" transform="rotate(120 12 12)" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>
          </button>
          <button type="button" class="device-tab" data-device="huawei" data-banner-index="${index}" title="Huawei">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/></svg>
          </button>
          <button type="button" class="device-tab" data-device="generic" data-banner-index="${index}" title="Generic">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12" y2="18"/></svg>
          </button>
        </div>
      </div>
      <div class="banner-lab-v4__phone-frame device-apple use-real-mockup aspect-9-16" data-banner-index="${index}">
        <div class="mockup-image-overlay"></div>
        <div class="phone-notch"></div>
        <div class="phone-screen">
          <div class="ig-feed-mockup" data-mockup-type="ig-feed" data-banner-index="${index}" style="display: none;">
            <div class="ig-feed__header">
              <div class="ig-feed__avatar"></div>
              <span class="ig-feed__username">your_brand</span>
            </div>
            <div class="ig-feed__image mockup-image-container" data-banner-index="${index}">
              <div class="mockup-placeholder">${state.locale === 'ja' ? '画像表示' : 'Image Preview'}</div>
            </div>
            <div class="ig-feed__actions">
              <div class="ig-feed__actions-left">
                <svg viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                <svg viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
                <svg viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/></svg>
              </div>
              <svg viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
            </div>
            <div class="ig-feed__likes">1,234 likes</div>
            <div class="ig-feed__caption"><span class="ig-feed__caption-user">your_brand</span>${state.locale === 'ja' ? 'キャプションを入力...' : 'Add caption...'}</div>
          </div>
          <div class="ig-reels-mockup" data-mockup-type="ig-reels" data-banner-index="${index}">
            <div class="ig-reels__image mockup-image-container" data-banner-index="${index}">
              <div class="mockup-placeholder">${state.locale === 'ja' ? '画像表示' : 'Image Preview'}</div>
            </div>
            <div class="ig-reels__sidebar">
              <div class="ig-reels__action"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg><span>24.5K</span></div>
              <div class="ig-reels__action"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg><span>892</span></div>
              <div class="ig-reels__audio-disc"></div>
            </div>
            <div class="ig-reels__bottom">
              <div class="ig-reels__user"><div class="ig-reels__avatar"></div><span>your_brand</span></div>
            </div>
          </div>
          <div class="tiktok-mockup" data-mockup-type="tiktok" data-banner-index="${index}" style="display: none;">
            <div class="tiktok__image mockup-image-container" data-banner-index="${index}">
              <div class="mockup-placeholder">${state.locale === 'ja' ? '画像表示' : 'Image Preview'}</div>
            </div>
            <div class="tiktok__sidebar">
              <div class="tiktok__avatar"></div>
              <div class="tiktok__action"><svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg><span>24.5K</span></div>
              <div class="tiktok__action"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg><span>892</span></div>
              <div class="tiktok__action"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><path d="M17 1l4 4-4 4"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><path d="M7 23l-4-4 4-4"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg><span>Share</span></div>
            </div>
            <div class="tiktok__bottom">
              <div class="tiktok__user">@your_brand</div>
            </div>
          </div>
        </div>
        <div class="phone-home-indicator"></div>
      </div>
      <button type="button" class="banner-lab-v4__mockup-download" data-banner-index="${index}">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
        </svg>
        Mockup
      </button>
    `;
    row.appendChild(mockupSection);

    // ===== Canvas Section =====
    const canvasSection = document.createElement('div');
    canvasSection.className = 'banner-lab-v4__canvas-section';
    canvasSection.innerHTML = `
      <div class="banner-lab-v4__canvas-header">
        <span class="banner-number">BANNER ${index + 1}</span>
        ${state.banners.length > 1 ? `
          <button type="button" class="banner-delete-btn" data-banner-index="${index}">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        ` : ''}
      </div>
      <div class="banner-lab-v4__canvas-wrapper">
        <div class="banner-lab-v4__panels-container" data-banner-index="${index}">
        </div>
        <canvas class="banner-lab-v4__output-canvas" data-banner-index="${index}" style="display: none;"></canvas>
      </div>
      <div class="banner-lab-v4__canvas-size">
        <span class="size-value">${banner.outputWidth} × ${banner.outputHeight}</span>
        <span class="size-unit">px</span>
      </div>
    `;
    row.appendChild(canvasSection);

    // Render canvas panels
    const panelsContainer = canvasSection.querySelector('.banner-lab-v4__panels-container');
    renderCanvasPanels(banner, index, panelsContainer);

    // ===== Controls Section =====
    const controlsSection = document.createElement('div');
    controlsSection.className = 'banner-lab-v4__controls-section';
    controlsSection.innerHTML = `
      <div class="control-group">
        <label>${state.locale === 'ja' ? 'アスペクト比' : 'ASPECT RATIO'}</label>
        <div class="ratio-buttons">
          <button type="button" class="ratio-btn ratio-btn--active" data-width="1080" data-height="1920" data-banner-index="${index}" title="9:16">
            <div class="ratio-icon ratio-icon--9-16"></div>
            <span class="ratio-label">9:16</span>
          </button>
          <button type="button" class="ratio-btn" data-width="1920" data-height="1080" data-banner-index="${index}" title="16:9">
            <div class="ratio-icon ratio-icon--16-9"></div>
            <span class="ratio-label">16:9</span>
          </button>
          <button type="button" class="ratio-btn" data-width="1080" data-height="1080" data-banner-index="${index}" title="1:1">
            <div class="ratio-icon ratio-icon--1-1"></div>
            <span class="ratio-label">1:1</span>
          </button>
          <button type="button" class="ratio-btn" data-width="1080" data-height="1350" data-banner-index="${index}" title="4:5">
            <div class="ratio-icon ratio-icon--4-5"></div>
            <span class="ratio-label">4:5</span>
          </button>
          <button type="button" class="ratio-btn" data-width="1200" data-height="628" data-banner-index="${index}" title="Wide">
            <div class="ratio-icon ratio-icon--wide"></div>
            <span class="ratio-label">Wide</span>
          </button>
        </div>
      </div>

      <div class="control-group">
        <label>${state.locale === 'ja' ? 'グリッド' : 'GRID'}</label>
        <div class="grid-buttons">
          <button type="button" class="grid-btn grid-btn--active" data-panels="1" data-banner-index="${index}">
            <svg viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" fill="none" stroke-width="1.5"/></svg>
          </button>
          <button type="button" class="grid-btn" data-panels="2" data-banner-index="${index}">
            <svg viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" fill="none" stroke-width="1.5"/><line x1="12" y1="4" x2="12" y2="20" stroke="currentColor" stroke-width="1.5"/></svg>
          </button>
          <button type="button" class="grid-btn" data-panels="3" data-banner-index="${index}">
            <svg viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" fill="none" stroke-width="1.5"/><line x1="10" y1="4" x2="10" y2="20" stroke="currentColor" stroke-width="1.5"/><line x1="10" y1="12" x2="20" y2="12" stroke="currentColor" stroke-width="1.5"/></svg>
          </button>
          <button type="button" class="grid-btn" data-panels="4" data-banner-index="${index}">
            <svg viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" fill="none" stroke-width="1.5"/><line x1="12" y1="4" x2="12" y2="20" stroke="currentColor" stroke-width="1.5"/><line x1="4" y1="12" x2="20" y2="12" stroke="currentColor" stroke-width="1.5"/></svg>
          </button>
        </div>
      </div>

      <div class="control-group layout-pattern-group" data-banner-index="${index}" style="display: none;">
        <label>${state.locale === 'ja' ? 'パターン' : 'PATTERN'}</label>
        <div class="pattern-buttons" data-banner-index="${index}">
        </div>
      </div>

      <div class="control-group grid-line-group" data-banner-index="${index}" style="display: none;">
        <label>${state.locale === 'ja' ? '区切り線' : 'DIVIDER'}</label>
        <div class="grid-line-inline-controls">
          <button type="button" class="grid-line-toggle-btn" data-banner-index="${index}" title="${state.locale === 'ja' ? '区切り線ON/OFF' : 'Toggle Divider'}">
            <svg class="grid-line-icon-off" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="4" x2="12" y2="20"/><line x1="4" y1="4" x2="4" y2="20" opacity="0.3"/><line x1="20" y1="4" x2="20" y2="20" opacity="0.3"/></svg>
            <svg class="grid-line-icon-on" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="4" x2="12" y2="20"/><line x1="4" y1="4" x2="4" y2="20"/><line x1="20" y1="4" x2="20" y2="20"/></svg>
          </button>
          <div class="grid-line-color-dots" data-banner-index="${index}">
            <span class="grid-line-dot active" data-color="#ffffff" style="background:#fff" title="White"></span>
            <span class="grid-line-dot" data-color="#000000" style="background:#000" title="Black"></span>
            <span class="grid-line-dot" data-color="#ff3b30" style="background:#ff3b30" title="Red"></span>
            <span class="grid-line-dot" data-color="#34c759" style="background:#34c759" title="Green"></span>
            <span class="grid-line-dot" data-color="#007aff" style="background:#007aff" title="Blue"></span>
          </div>
          <span class="grid-line-width-label">${state.locale === 'ja' ? '太さ' : 'W'}</span>
          <input type="range" class="grid-line-width-slider" data-banner-index="${index}" min="1" max="12" value="2" title="${state.locale === 'ja' ? '線の太さ' : 'Line Width'}">
          <span class="grid-line-width-value">2px</span>
          <button type="button" class="grid-line-reset-btn" data-banner-index="${index}" title="${state.locale === 'ja' ? '位置をリセット' : 'Reset Position'}">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
          </button>
        </div>
        <p class="grid-line-hint" style="font-size:10px;color:var(--lab-text-muted);margin-top:4px;">${state.locale === 'ja' ? '※線をドラッグして位置調整可能' : '※Drag line to adjust position'}</p>
      </div>

      <div class="control-group">
        <label>${state.locale === 'ja' ? '形式' : 'FORMAT'}</label>
        <div class="format-buttons">
          <button type="button" class="format-btn format-btn--active" data-format="png" data-banner-index="${index}">PNG</button>
          <button type="button" class="format-btn" data-format="jpeg" data-banner-index="${index}">JPEG</button>
          <button type="button" class="format-btn${state.supportsWebP ? '' : ' format-btn--disabled'}" data-format="webp" data-banner-index="${index}" ${state.supportsWebP ? '' : 'title="WebP not supported in this browser"'}>WebP${state.supportsWebP ? '' : '*'}</button>
        </div>
      </div>

      <div class="control-group texture-group">
        <label>${state.locale === 'ja' ? 'テクスチャ' : 'TEXTURE'}</label>
        <div class="texture-controls">
          <button type="button" class="texture-btn" data-texture="digital" data-banner-index="${index}" title="${state.locale === 'ja' ? 'デジタルノイズ' : 'Digital Noise'}">
            <svg class="texture-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <circle cx="7" cy="7" r="1" fill="currentColor"/>
              <circle cx="12" cy="7" r="1" fill="currentColor"/>
              <circle cx="17" cy="7" r="1" fill="currentColor"/>
              <circle cx="7" cy="12" r="1" fill="currentColor"/>
              <circle cx="12" cy="12" r="1" fill="currentColor"/>
              <circle cx="17" cy="12" r="1" fill="currentColor"/>
              <circle cx="7" cy="17" r="1" fill="currentColor"/>
              <circle cx="12" cy="17" r="1" fill="currentColor"/>
              <circle cx="17" cy="17" r="1" fill="currentColor"/>
            </svg>
            <span class="texture-btn-label">Digital</span>
          </button>
          <button type="button" class="texture-btn" data-texture="film" data-banner-index="${index}" title="${state.locale === 'ja' ? 'フィルムグレイン' : 'Film Grain'}">
            <svg class="texture-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <rect x="2" y="2" width="20" height="20" rx="2"/>
              <circle cx="7" cy="7" r="2"/>
              <circle cx="7" cy="17" r="2"/>
              <line x1="10" y1="2" x2="10" y2="22"/>
              <line x1="14" y1="2" x2="14" y2="22"/>
            </svg>
            <span class="texture-btn-label">Film</span>
          </button>
          <button type="button" class="texture-btn" data-texture="paper" data-banner-index="${index}" title="${state.locale === 'ja' ? '紙テクスチャ' : 'Paper Texture'}">
            <svg class="texture-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="8" y1="13" x2="16" y2="13" stroke-dasharray="2 2"/>
              <line x1="8" y1="17" x2="14" y2="17" stroke-dasharray="2 2"/>
            </svg>
            <span class="texture-btn-label">Paper</span>
          </button>
        </div>
        <div class="texture-intensity-wrapper" data-banner-index="${index}" style="display: none;">
          <span class="texture-intensity-label">${state.locale === 'ja' ? '強度' : 'Intensity'}</span>
          <input type="range" class="texture-intensity-slider" data-banner-index="${index}" min="10" max="100" value="50">
          <span class="texture-intensity-value">50%</span>
        </div>
      </div>

      <button type="button" class="banner-lab-v4__export-btn" data-banner-index="${index}">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
        </svg>
        DOWNLOAD
      </button>
    `;
    row.appendChild(controlsSection);

    return row;
  }

  // ============================================
  // Update Panel Media Position (Lightweight - no DOM rebuild)
  // Used during drag/zoom to avoid flicker
  // ============================================
  function updatePanelMediaPosition(banner, bannerIndex, panelIndex, container) {
    const wrapperWidth = 460;
    const wrapperHeight = 420;

    const outputAspect = banner.outputWidth / banner.outputHeight;
    const containerAspect = wrapperWidth / wrapperHeight;

    let displayWidth;
    if (outputAspect > containerAspect) {
      displayWidth = wrapperWidth;
    } else {
      displayWidth = wrapperHeight * outputAspect;
    }

    const displayScale = displayWidth / banner.outputWidth;

    const panelEl = container.querySelector(`.v0-banner-creator__panel[data-panel-index="${panelIndex}"]`);
    if (!panelEl) return;

    const mediaEl = panelEl.querySelector('.v0-banner-creator__panel-image');
    if (!mediaEl) return;

    const panelData = banner.panels[panelIndex];
    if (!panelData || !panelData.image) return;

    const imgWidth = panelData.naturalWidth * panelData.scale * displayScale;
    const imgHeight = panelData.naturalHeight * panelData.scale * displayScale;
    const imgX = panelData.x * displayScale;
    const imgY = panelData.y * displayScale;

    mediaEl.style.width = imgWidth + 'px';
    mediaEl.style.height = imgHeight + 'px';
    mediaEl.style.left = imgX + 'px';
    mediaEl.style.top = imgY + 'px';
  }

  // ============================================
  // Render Canvas Panels
  // ============================================
  function renderCanvasPanels(banner, bannerIndex, container) {
    // Calculate display size - balanced for UI fit and editing experience
    const wrapperWidth = 460;
    const wrapperHeight = 420;

    const outputAspect = banner.outputWidth / banner.outputHeight;
    const containerAspect = wrapperWidth / wrapperHeight;

    let displayWidth, displayHeight;
    if (outputAspect > containerAspect) {
      displayWidth = wrapperWidth;
      displayHeight = wrapperWidth / outputAspect;
    } else {
      displayHeight = wrapperHeight;
      displayWidth = wrapperHeight * outputAspect;
    }

    const displayScale = displayWidth / banner.outputWidth;

    container.style.width = displayWidth + 'px';
    container.style.height = displayHeight + 'px';

    // IMPORTANT: Don't use innerHTML = '' - it destroys grid lines!
    // Instead, selectively remove only panel elements
    const existingPanels = container.querySelectorAll('.v0-banner-creator__panel');
    existingPanels.forEach(panel => panel.remove());

    // Also remove existing grid lines (we'll recreate them at the end)
    const existingGridLines = container.querySelector('.v0-banner-creator__grid-lines');
    if (existingGridLines) existingGridLines.remove();

    // Create panels
    banner.selectedLayout.panels.forEach((panelDef, panelIndex) => {
      const panelEl = document.createElement('div');
      panelEl.className = 'v0-banner-creator__panel';
      panelEl.dataset.panelIndex = panelIndex;
      panelEl.dataset.bannerIndex = bannerIndex;

      const x = panelDef.x * displayWidth;
      const y = panelDef.y * displayHeight;
      const w = panelDef.w * displayWidth;
      const h = panelDef.h * displayHeight;

      panelEl.style.left = x + 'px';
      panelEl.style.top = y + 'px';
      panelEl.style.width = w + 'px';
      panelEl.style.height = h + 'px';

      // Panel number
      if (banner.panelCount > 1) {
        const numberEl = document.createElement('div');
        numberEl.className = 'v0-banner-creator__panel-number';
        numberEl.textContent = panelIndex + 1;
        panelEl.appendChild(numberEl);
      }

      // Panel content
      const panelData = banner.panels[panelIndex];
      if (panelData && panelData.image) {
        let mediaEl;

        if (panelData.isVideo) {
          // Video element
          mediaEl = document.createElement('video');
          mediaEl.className = 'v0-banner-creator__panel-image v0-banner-creator__panel-video';
          mediaEl.src = panelData.image;
          mediaEl.muted = true;
          mediaEl.loop = true;
          mediaEl.playsInline = true;
          mediaEl.autoplay = false; // Controlled by Intersection Observer
          mediaEl.draggable = false;
          // Click to toggle play/pause
          mediaEl.addEventListener('click', (e) => {
            e.stopPropagation();
            if (mediaEl.paused) {
              mediaEl.play().catch(() => {});
            } else {
              mediaEl.pause();
            }
          });
          mediaEl.style.cursor = 'pointer';
          mediaEl.title = state.locale === 'ja' ? 'クリックで再生/停止' : 'Click to play/pause';

          // Check if banner row is currently visible and auto-play if so
          const bannerRow = container.closest('.banner-lab-v4__banner-row');
          if (bannerRow && bannerRowObserver) {
            // Trigger initial visibility check after video loads
            mediaEl.addEventListener('loadeddata', () => {
              const rect = bannerRow.getBoundingClientRect();
              const scrollContainer = document.getElementById('bannerScrollContainer');
              if (scrollContainer) {
                const containerRect = scrollContainer.getBoundingClientRect();
                // Check if row is within visible area
                if (rect.top < containerRect.bottom && rect.bottom > containerRect.top) {
                  mediaEl.play().catch(() => {});
                }
              }
            }, { once: true });
          }
        } else {
          // Image element
          mediaEl = document.createElement('img');
          mediaEl.className = 'v0-banner-creator__panel-image';
          mediaEl.src = panelData.image;
          mediaEl.draggable = false;
        }

        const imgWidth = panelData.naturalWidth * panelData.scale * displayScale;
        const imgHeight = panelData.naturalHeight * panelData.scale * displayScale;
        const imgX = panelData.x * displayScale;
        const imgY = panelData.y * displayScale;

        mediaEl.style.width = imgWidth + 'px';
        mediaEl.style.height = imgHeight + 'px';
        mediaEl.style.left = imgX + 'px';
        mediaEl.style.top = imgY + 'px';

        panelEl.appendChild(mediaEl);

        // Panel action buttons container
        const actionBtns = document.createElement('div');
        actionBtns.className = 'v0-banner-creator__panel-actions';

        // Replace button
        const replaceBtn = document.createElement('button');
        replaceBtn.type = 'button';
        replaceBtn.className = 'v0-banner-creator__panel-replace';
        replaceBtn.dataset.panelIndex = panelIndex;
        replaceBtn.dataset.bannerIndex = bannerIndex;
        replaceBtn.innerHTML = `
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
            <path d="M3 3v5h5"/>
            <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/>
            <path d="M16 21h5v-5"/>
          </svg>
        `;
        replaceBtn.title = state.locale === 'ja' ? '差し替え' : 'Replace';
        actionBtns.appendChild(replaceBtn);

        // Delete button for panel content
        const deleteBtn = document.createElement('button');
        deleteBtn.type = 'button';
        deleteBtn.className = 'v0-banner-creator__panel-delete';
        deleteBtn.dataset.panelIndex = panelIndex;
        deleteBtn.dataset.bannerIndex = bannerIndex;
        deleteBtn.innerHTML = `
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        `;
        deleteBtn.title = state.locale === 'ja' ? (panelData.isVideo ? '動画を削除' : '画像を削除') : (panelData.isVideo ? 'Remove video' : 'Remove image');
        actionBtns.appendChild(deleteBtn);

        panelEl.appendChild(actionBtns);
      } else {
        const placeholder = document.createElement('div');
        placeholder.className = 'v0-banner-creator__panel-placeholder';
        placeholder.innerHTML = `
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <path d="M21 15l-5-5L5 21"/>
          </svg>
          <span>${state.locale === 'ja' ? '画像/動画を追加' : 'Add image/video'}</span>
        `;
        panelEl.appendChild(placeholder);
      }

      // File input (accepts both images and videos)
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = 'image/*,video/*';
      fileInput.className = 'v0-banner-creator__file-input';
      fileInput.dataset.panelIndex = panelIndex;
      fileInput.dataset.bannerIndex = bannerIndex;
      panelEl.appendChild(fileInput);

      container.appendChild(panelEl);
    });

    // Draw grid lines overlay (if enabled and multiple panels)
    if (banner.gridLineEnabled && banner.panelCount > 1) {
      const gridLinesEl = document.createElement('div');
      gridLinesEl.className = 'v0-banner-creator__grid-lines';

      // Calculate grid line positions based on layout with custom offsets
      const layout = banner.selectedLayout;
      const linePositions = calculateGridLinePositions(layout, displayWidth, displayHeight);

      linePositions.forEach((line, lineIndex) => {
        const lineEl = document.createElement('div');
        lineEl.className = 'v0-banner-creator__grid-line v0-banner-creator__grid-line--draggable';
        lineEl.style.position = 'absolute';
        // Note: backgroundColor is NOT set on outer container - only inner div has the color
        lineEl.dataset.lineType = line.type;
        lineEl.dataset.lineIndex = lineIndex;
        lineEl.dataset.originalPosition = line.originalPosition;
        lineEl.dataset.bannerIndex = bannerIndex;

        // Hit area is larger than visual line for easier dragging
        const hitAreaSize = Math.max(banner.gridLineWidth, 12);

        if (line.type === 'vertical') {
          // Vertical line: use exact start/end from line calculation
          const lineHeight = line.end - line.start;

          lineEl.style.left = (line.position - hitAreaSize / 2) + 'px';
          lineEl.style.top = line.start + 'px';
          lineEl.style.width = hitAreaSize + 'px';
          lineEl.style.height = lineHeight + 'px';
          lineEl.style.cursor = 'ew-resize';
          // Visual line inside hit area - centered horizontally
          lineEl.innerHTML = `<div style="position:absolute;left:50%;top:0;transform:translateX(-50%);width:${banner.gridLineWidth}px;height:100%;background:${banner.gridLineColor};pointer-events:none;"></div>`;
        } else {
          // Horizontal line: use exact start/end from line calculation
          const lineWidth = line.end - line.start;

          lineEl.style.left = line.start + 'px';
          lineEl.style.top = (line.position - hitAreaSize / 2) + 'px';
          lineEl.style.width = lineWidth + 'px';
          lineEl.style.height = hitAreaSize + 'px';
          lineEl.style.cursor = 'ns-resize';
          // Visual line inside hit area - centered vertically
          lineEl.innerHTML = `<div style="position:absolute;left:0;top:50%;transform:translateY(-50%);width:100%;height:${banner.gridLineWidth}px;background:${banner.gridLineColor};pointer-events:none;"></div>`;
        }

        // Add drag functionality
        lineEl.addEventListener('mousedown', (e) => {
          e.preventDefault();
          e.stopPropagation();
          startGridLineDrag(e, lineEl, banner, bannerIndex, container, displayWidth, displayHeight);
        });

        // Touch support
        lineEl.addEventListener('touchstart', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const touch = e.touches[0];
          startGridLineDrag(touch, lineEl, banner, bannerIndex, container, displayWidth, displayHeight);
        }, { passive: false });

        gridLinesEl.appendChild(lineEl);
      });

      container.appendChild(gridLinesEl);
    }
  }

  // Handle grid line dragging - updates actual panel layout in REAL-TIME
  // Each line moves INDEPENDENTLY, and connected lines' endpoints are updated
  function startGridLineDrag(startEvent, lineEl, banner, bannerIndex, container, displayWidth, displayHeight) {
    const lineType = lineEl.dataset.lineType;
    const originalPosition = parseFloat(lineEl.dataset.originalPosition);
    const lineIndex = parseInt(lineEl.dataset.lineIndex, 10);
    const rect = container.getBoundingClientRect();

    const startX = startEvent.clientX || startEvent.pageX;
    const startY = startEvent.clientY || startEvent.pageY;
    const startPos = lineType === 'vertical' ? parseFloat(lineEl.style.left) : parseFloat(lineEl.style.top);

    // Get grid lines container
    const gridLinesEl = container.querySelector('.v0-banner-creator__grid-lines');

    // Get panel elements for real-time update
    const panelElements = container.querySelectorAll('.v0-banner-creator__panel');
    const row = container.closest('.banner-lab-v4__banner-row');

    // Store initial panel layout for calculating changes
    const initialPanels = banner.selectedLayout.panels.map(p => ({ ...p }));

    // Store initial line data (start/end for all lines, for connected line endpoint updates)
    const allLines = gridLinesEl ? Array.from(gridLinesEl.querySelectorAll('.v0-banner-creator__grid-line')) : [];
    const initialLineData = allLines.map(line => ({
      element: line,
      type: line.dataset.lineType,
      originalPos: parseFloat(line.dataset.originalPosition),
      left: parseFloat(line.style.left) || 0,
      top: parseFloat(line.style.top) || 0,
      width: parseFloat(line.style.width) || 0,
      height: parseFloat(line.style.height) || 0
    }));

    // Add visual feedback ONLY to dragged line
    lineEl.classList.add('dragging');
    document.body.style.cursor = lineType === 'vertical' ? 'ew-resize' : 'ns-resize';

    let lastNewRatio = originalPosition;

    function onMove(e) {
      const clientX = e.clientX || (e.touches && e.touches[0].clientX) || startX;
      const clientY = e.clientY || (e.touches && e.touches[0].clientY) || startY;

      // Calculate movement delta
      let delta;
      if (lineType === 'vertical') {
        delta = clientX - startX;
      } else {
        delta = clientY - startY;
      }

      // Calculate new position for THIS line only
      // Limit movement to prevent lines from going too close to edges (min 15%, max 85%)
      // This ensures panels always have a visible minimum size
      let newPos = startPos + delta;
      const minLimit = 0.15; // 15% from edge minimum
      const maxLimit = 0.85; // 85% from edge maximum
      if (lineType === 'vertical') {
        newPos = Math.max(displayWidth * minLimit, Math.min(displayWidth * maxLimit, newPos));
        lineEl.style.left = newPos + 'px';
      } else {
        newPos = Math.max(displayHeight * minLimit, Math.min(displayHeight * maxLimit, newPos));
        lineEl.style.top = newPos + 'px';
      }

      // Calculate new ratio for this line
      const newRatio = lineType === 'vertical' ? newPos / displayWidth : newPos / displayHeight;
      lastNewRatio = newRatio;

      // Update connected lines' endpoints (other lines that touch this line)
      updateConnectedLineEndpoints(initialLineData, lineEl, lineType, originalPosition, newRatio, displayWidth, displayHeight);

      // Update panel DOM elements in real-time (only affects panels touching this line)
      updatePanelElementsForSingleLine(panelElements, initialPanels, originalPosition, newRatio, lineType, displayWidth, displayHeight);
    }

    function onEnd(e) {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);

      // Remove visual feedback
      lineEl.classList.remove('dragging');
      document.body.style.cursor = '';

      // Update actual panel layout data for this single line
      updatePanelLayoutForSingleLine(banner, originalPosition, lastNewRatio, lineType);

      // Update line's original position for next drag
      lineEl.dataset.originalPosition = lastNewRatio;

      // Re-render grid lines to fix all positions and endpoints correctly
      rerenderGridLines(banner, bannerIndex, container, displayWidth, displayHeight);

      // Update mockup preview
      updateMockupPreview(bannerIndex, row);
    }

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onEnd);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
  }

  // Update endpoints of lines that are connected to the dragged line
  function updateConnectedLineEndpoints(initialLineData, draggedLineEl, draggedLineType, originalPos, newPos, displayWidth, displayHeight) {
    const eps = 0.02; // tolerance for position matching

    initialLineData.forEach(lineData => {
      if (lineData.element === draggedLineEl) return; // Skip the dragged line itself

      // If dragging a vertical line, update horizontal lines that start/end at this position
      if (draggedLineType === 'vertical' && lineData.type === 'horizontal') {
        const startXRatio = lineData.left / displayWidth;
        const endXRatio = (lineData.left + lineData.width) / displayWidth;

        // If horizontal line's left edge is at the dragged line's position
        if (Math.abs(startXRatio - originalPos) < eps) {
          const newLeft = newPos * displayWidth;
          lineData.element.style.left = newLeft + 'px';
          lineData.element.style.width = (lineData.left + lineData.width - newLeft) + 'px';
        }
        // If horizontal line's right edge is at the dragged line's position
        else if (Math.abs(endXRatio - originalPos) < eps) {
          const newWidth = newPos * displayWidth - lineData.left;
          lineData.element.style.width = newWidth + 'px';
        }
      }

      // If dragging a horizontal line, update vertical lines that start/end at this position
      if (draggedLineType === 'horizontal' && lineData.type === 'vertical') {
        const startYRatio = lineData.top / displayHeight;
        const endYRatio = (lineData.top + lineData.height) / displayHeight;

        // If vertical line's top edge is at the dragged line's position
        if (Math.abs(startYRatio - originalPos) < eps) {
          const newTop = newPos * displayHeight;
          lineData.element.style.top = newTop + 'px';
          lineData.element.style.height = (lineData.top + lineData.height - newTop) + 'px';
        }
        // If vertical line's bottom edge is at the dragged line's position
        else if (Math.abs(endYRatio - originalPos) < eps) {
          const newHeight = newPos * displayHeight - lineData.top;
          lineData.element.style.height = newHeight + 'px';
        }
      }
    });
  }

  // Update panel elements for a single line movement (not all lines)
  function updatePanelElementsForSingleLine(panelElements, initialPanels, originalPos, newPos, lineType, displayWidth, displayHeight) {
    const eps = 0.01;

    initialPanels.forEach((panel, index) => {
      const panelEl = panelElements[index];
      if (!panelEl) return;

      let newX = panel.x;
      let newY = panel.y;
      let newW = panel.w;
      let newH = panel.h;

      if (lineType === 'vertical') {
        // If panel's right edge is at original position, adjust width
        if (Math.abs(panel.x + panel.w - originalPos) < eps) {
          newW = newPos - panel.x;
          if (newW < 0.1) newW = 0.1;
        }
        // If panel's left edge is at original position, adjust x and width
        else if (Math.abs(panel.x - originalPos) < eps) {
          const widthChange = newPos - panel.x;
          newX = newPos;
          newW = panel.w - widthChange;
          if (newW < 0.1) newW = 0.1;
        }
      } else {
        // If panel's bottom edge is at original position, adjust height
        if (Math.abs(panel.y + panel.h - originalPos) < eps) {
          newH = newPos - panel.y;
          if (newH < 0.1) newH = 0.1;
        }
        // If panel's top edge is at original position, adjust y and height
        else if (Math.abs(panel.y - originalPos) < eps) {
          const heightChange = newPos - panel.y;
          newY = newPos;
          newH = panel.h - heightChange;
          if (newH < 0.1) newH = 0.1;
        }
      }

      // Apply styles directly to panel element
      panelEl.style.left = (newX * displayWidth) + 'px';
      panelEl.style.top = (newY * displayHeight) + 'px';
      panelEl.style.width = (newW * displayWidth) + 'px';
      panelEl.style.height = (newH * displayHeight) + 'px';
    });
  }

  // Update panel layout for a single line movement
  function updatePanelLayoutForSingleLine(banner, originalPosition, newPosition, lineType) {
    const layout = banner.selectedLayout;
    const panels = layout.panels;
    const eps = 0.01;

    if (lineType === 'vertical') {
      panels.forEach(panel => {
        // If panel's right edge is at original position, adjust width
        if (Math.abs(panel.x + panel.w - originalPosition) < eps) {
          const newWidth = newPosition - panel.x;
          if (newWidth > 0.1) {
            panel.w = newWidth;
          }
        }
        // If panel's left edge is at original position, adjust x and width
        else if (Math.abs(panel.x - originalPosition) < eps) {
          const widthChange = newPosition - panel.x;
          panel.x = newPosition;
          panel.w = panel.w - widthChange;
          if (panel.w < 0.1) panel.w = 0.1;
        }
      });
    } else {
      // Horizontal line - affects y and h
      panels.forEach(panel => {
        // If panel's bottom edge is at original position, adjust height
        if (Math.abs(panel.y + panel.h - originalPosition) < eps) {
          const newHeight = newPosition - panel.y;
          if (newHeight > 0.1) {
            panel.h = newHeight;
          }
        }
        // If panel's top edge is at original position, adjust y and height
        else if (Math.abs(panel.y - originalPosition) < eps) {
          const heightChange = newPosition - panel.y;
          panel.y = newPosition;
          panel.h = panel.h - heightChange;
          if (panel.h < 0.1) panel.h = 0.1;
        }
      });
    }
  }

  // Re-render grid lines after drag ends to ensure correct positions
  function rerenderGridLines(banner, bannerIndex, container, displayWidth, displayHeight) {
    // Remove existing grid lines
    const existingGridLines = container.querySelector('.v0-banner-creator__grid-lines');
    if (existingGridLines) existingGridLines.remove();

    // Only add grid lines if enabled and multiple panels
    if (!banner.gridLineEnabled || banner.panelCount <= 1) return;

    const gridLinesEl = document.createElement('div');
    gridLinesEl.className = 'v0-banner-creator__grid-lines';

    // Calculate grid line positions based on updated layout
    const linePositions = calculateGridLinePositions(banner.selectedLayout, displayWidth, displayHeight);

    linePositions.forEach((line, lineIndex) => {
      const lineEl = document.createElement('div');
      lineEl.className = 'v0-banner-creator__grid-line v0-banner-creator__grid-line--draggable';
      lineEl.style.position = 'absolute';
      // Note: backgroundColor is NOT set on outer container - only inner div has the color
      lineEl.dataset.lineType = line.type;
      lineEl.dataset.lineIndex = lineIndex;
      lineEl.dataset.originalPosition = line.originalPosition;
      lineEl.dataset.bannerIndex = bannerIndex;

      if (line.type === 'vertical') {
        const hitAreaWidth = Math.max(banner.gridLineWidth, 12);
        lineEl.style.left = (line.position - hitAreaWidth / 2) + 'px';
        lineEl.style.top = line.start + 'px';
        lineEl.style.width = hitAreaWidth + 'px';
        lineEl.style.height = (line.end - line.start) + 'px';
        lineEl.style.cursor = 'ew-resize';
        lineEl.innerHTML = `<div style="position:absolute;left:50%;transform:translateX(-50%);width:${banner.gridLineWidth}px;height:100%;background:${banner.gridLineColor};pointer-events:none;"></div>`;
      } else {
        const hitAreaHeight = Math.max(banner.gridLineWidth, 12);
        lineEl.style.left = line.start + 'px';
        lineEl.style.top = (line.position - hitAreaHeight / 2) + 'px';
        const lineWidth = line.end - line.start;
        lineEl.style.width = lineWidth + 'px';
        lineEl.style.height = hitAreaHeight + 'px';
        lineEl.style.cursor = 'ns-resize';
        // Use explicit pixel width instead of 100% to avoid CSS inheritance issues
        lineEl.innerHTML = `<div style="position:absolute;top:50%;transform:translateY(-50%);width:${lineWidth}px;height:${banner.gridLineWidth}px;background:${banner.gridLineColor};pointer-events:none;"></div>`;
      }

      // Add drag functionality
      lineEl.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        startGridLineDrag(e, lineEl, banner, bannerIndex, container, displayWidth, displayHeight);
      });

      // Touch support
      lineEl.addEventListener('touchstart', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const touch = e.touches[0];
        startGridLineDrag(touch, lineEl, banner, bannerIndex, container, displayWidth, displayHeight);
      }, { passive: false });

      gridLinesEl.appendChild(lineEl);
    });

    container.appendChild(gridLinesEl);
  }

  // Real-time update of panel elements during drag (no DOM recreation)
  function updatePanelElementsRealtime(panelElements, initialPanels, originalPos, newPos, lineType, displayWidth, displayHeight) {
    const eps = 0.01;

    initialPanels.forEach((panel, index) => {
      const panelEl = panelElements[index];
      if (!panelEl) return;

      let newX = panel.x;
      let newY = panel.y;
      let newW = panel.w;
      let newH = panel.h;

      if (lineType === 'vertical') {
        // If panel's right edge is at original position, adjust width
        if (Math.abs(panel.x + panel.w - originalPos) < eps) {
          newW = newPos - panel.x;
          if (newW < 0.1) newW = 0.1;
        }
        // If panel's left edge is at original position, adjust x and width
        else if (Math.abs(panel.x - originalPos) < eps) {
          const widthChange = newPos - panel.x;
          newX = newPos;
          newW = panel.w - widthChange;
          if (newW < 0.1) newW = 0.1;
        }
      } else {
        // If panel's bottom edge is at original position, adjust height
        if (Math.abs(panel.y + panel.h - originalPos) < eps) {
          newH = newPos - panel.y;
          if (newH < 0.1) newH = 0.1;
        }
        // If panel's top edge is at original position, adjust y and height
        else if (Math.abs(panel.y - originalPos) < eps) {
          const heightChange = newPos - panel.y;
          newY = newPos;
          newH = panel.h - heightChange;
          if (newH < 0.1) newH = 0.1;
        }
      }

      // Apply styles directly to panel element
      panelEl.style.left = (newX * displayWidth) + 'px';
      panelEl.style.top = (newY * displayHeight) + 'px';
      panelEl.style.width = (newW * displayWidth) + 'px';
      panelEl.style.height = (newH * displayHeight) + 'px';
    });
  }

  // Update panel layout when grid line is moved
  function updatePanelLayoutFromGridLine(banner, originalPosition, newPosition, lineType) {
    const layout = banner.selectedLayout;
    const panels = layout.panels;
    const eps = 0.01;

    if (lineType === 'vertical') {
      // Find panels that have edges at originalPosition
      panels.forEach(panel => {
        // If panel's right edge is at original position, adjust width
        if (Math.abs(panel.x + panel.w - originalPosition) < eps) {
          const newWidth = newPosition - panel.x;
          if (newWidth > 0.1) {
            panel.w = newWidth;
          }
        }
        // If panel's left edge is at original position, adjust x and width
        else if (Math.abs(panel.x - originalPosition) < eps) {
          const widthChange = newPosition - panel.x;
          panel.x = newPosition;
          panel.w = panel.w - widthChange;
          if (panel.w < 0.1) panel.w = 0.1;
        }
      });
    } else {
      // Horizontal line - affects y and h
      panels.forEach(panel => {
        // If panel's bottom edge is at original position, adjust height
        if (Math.abs(panel.y + panel.h - originalPosition) < eps) {
          const newHeight = newPosition - panel.y;
          if (newHeight > 0.1) {
            panel.h = newHeight;
          }
        }
        // If panel's top edge is at original position, adjust y and height
        else if (Math.abs(panel.y - originalPosition) < eps) {
          const heightChange = newPosition - panel.y;
          panel.y = newPosition;
          panel.h = panel.h - heightChange;
          if (panel.h < 0.1) panel.h = 0.1;
        }
      });
    }
  }

  // Real-time update of ALL panel elements when ALL grid lines move together
  // This properly adjusts both positions and sizes to maintain continuous layout
  function updatePanelElementsRealtimeAll(panelElements, initialPanels, deltaRatio, lineType, displayWidth, displayHeight) {
    if (lineType === 'vertical') {
      // Find all internal x boundaries (not at 0 or 1)
      const sortedPanels = [...initialPanels].sort((a, b) => a.x - b.x);
      const internalBoundaries = new Set();
      sortedPanels.forEach(p => {
        if (p.x > 0.01 && p.x < 0.99) internalBoundaries.add(p.x);
        const rightEdge = p.x + p.w;
        if (rightEdge > 0.01 && rightEdge < 0.99) internalBoundaries.add(rightEdge);
      });

      const boundaryArray = [...internalBoundaries].sort((a, b) => a - b);
      const newBoundaries = boundaryArray.map(b => Math.max(0.05, Math.min(0.95, b + deltaRatio)));

      initialPanels.forEach((panel, index) => {
        const panelEl = panelElements[index];
        if (!panelEl) return;

        const oldLeft = panel.x;
        const oldRight = panel.x + panel.w;

        let newLeft = oldLeft;
        if (oldLeft > 0.01) {
          const boundaryIdx = boundaryArray.findIndex(b => Math.abs(b - oldLeft) < 0.01);
          if (boundaryIdx >= 0) newLeft = newBoundaries[boundaryIdx];
        }

        let newRight = oldRight;
        if (oldRight < 0.99) {
          const boundaryIdx = boundaryArray.findIndex(b => Math.abs(b - oldRight) < 0.01);
          if (boundaryIdx >= 0) newRight = newBoundaries[boundaryIdx];
        } else {
          newRight = 1;
        }

        const newX = Math.max(0, newLeft);
        const newW = newRight - newLeft;

        panelEl.style.left = (newX * displayWidth) + 'px';
        panelEl.style.top = (panel.y * displayHeight) + 'px';
        panelEl.style.width = (newW * displayWidth) + 'px';
        panelEl.style.height = (panel.h * displayHeight) + 'px';
      });
    } else {
      // For vertical layouts (horizontal lines)
      const sortedPanels = [...initialPanels].sort((a, b) => a.y - b.y);
      const internalBoundaries = new Set();
      sortedPanels.forEach(p => {
        if (p.y > 0.01 && p.y < 0.99) internalBoundaries.add(p.y);
        const bottomEdge = p.y + p.h;
        if (bottomEdge > 0.01 && bottomEdge < 0.99) internalBoundaries.add(bottomEdge);
      });

      const boundaryArray = [...internalBoundaries].sort((a, b) => a - b);
      const newBoundaries = boundaryArray.map(b => Math.max(0.05, Math.min(0.95, b + deltaRatio)));

      initialPanels.forEach((panel, index) => {
        const panelEl = panelElements[index];
        if (!panelEl) return;

        const oldTop = panel.y;
        const oldBottom = panel.y + panel.h;

        let newTop = oldTop;
        if (oldTop > 0.01) {
          const boundaryIdx = boundaryArray.findIndex(b => Math.abs(b - oldTop) < 0.01);
          if (boundaryIdx >= 0) newTop = newBoundaries[boundaryIdx];
        }

        let newBottom = oldBottom;
        if (oldBottom < 0.99) {
          const boundaryIdx = boundaryArray.findIndex(b => Math.abs(b - oldBottom) < 0.01);
          if (boundaryIdx >= 0) newBottom = newBoundaries[boundaryIdx];
        } else {
          newBottom = 1;
        }

        const newY = Math.max(0, newTop);
        const newH = newBottom - newTop;

        panelEl.style.left = (panel.x * displayWidth) + 'px';
        panelEl.style.top = (newY * displayHeight) + 'px';
        panelEl.style.width = (panel.w * displayWidth) + 'px';
        panelEl.style.height = (newH * displayHeight) + 'px';
      });
    }
  }

  // Update panel layout when ALL grid lines move together
  // This properly adjusts both positions and sizes to maintain continuous layout
  function updatePanelLayoutFromGridLineAll(banner, deltaRatio, lineType) {
    const layout = banner.selectedLayout;
    const panels = layout.panels;

    if (lineType === 'vertical') {
      // For horizontal layouts (panels arranged left to right)
      // Sort panels by x position
      const sortedPanels = [...panels].sort((a, b) => a.x - b.x);

      // Find all internal boundaries (not at 0 or 1)
      const internalBoundaries = new Set();
      sortedPanels.forEach(p => {
        if (p.x > 0.01 && p.x < 0.99) internalBoundaries.add(p.x);
        const rightEdge = p.x + p.w;
        if (rightEdge > 0.01 && rightEdge < 0.99) internalBoundaries.add(rightEdge);
      });

      // Move all internal boundaries
      const boundaryArray = [...internalBoundaries].sort((a, b) => a - b);
      const newBoundaries = boundaryArray.map(b => Math.max(0.05, Math.min(0.95, b + deltaRatio)));

      // Update panels based on new boundaries
      sortedPanels.forEach((panel, idx) => {
        const oldLeft = panel.x;
        const oldRight = panel.x + panel.w;

        // Find new left boundary
        let newLeft = oldLeft;
        if (oldLeft > 0.01) {
          const boundaryIdx = boundaryArray.findIndex(b => Math.abs(b - oldLeft) < 0.01);
          if (boundaryIdx >= 0) newLeft = newBoundaries[boundaryIdx];
        }

        // Find new right boundary
        let newRight = oldRight;
        if (oldRight < 0.99) {
          const boundaryIdx = boundaryArray.findIndex(b => Math.abs(b - oldRight) < 0.01);
          if (boundaryIdx >= 0) newRight = newBoundaries[boundaryIdx];
        } else {
          newRight = 1; // Keep at edge
        }

        panel.x = newLeft;
        panel.w = newRight - newLeft;
      });
    } else {
      // For vertical layouts (panels arranged top to bottom)
      const sortedPanels = [...panels].sort((a, b) => a.y - b.y);

      const internalBoundaries = new Set();
      sortedPanels.forEach(p => {
        if (p.y > 0.01 && p.y < 0.99) internalBoundaries.add(p.y);
        const bottomEdge = p.y + p.h;
        if (bottomEdge > 0.01 && bottomEdge < 0.99) internalBoundaries.add(bottomEdge);
      });

      const boundaryArray = [...internalBoundaries].sort((a, b) => a - b);
      const newBoundaries = boundaryArray.map(b => Math.max(0.05, Math.min(0.95, b + deltaRatio)));

      sortedPanels.forEach(panel => {
        const oldTop = panel.y;
        const oldBottom = panel.y + panel.h;

        let newTop = oldTop;
        if (oldTop > 0.01) {
          const boundaryIdx = boundaryArray.findIndex(b => Math.abs(b - oldTop) < 0.01);
          if (boundaryIdx >= 0) newTop = newBoundaries[boundaryIdx];
        }

        let newBottom = oldBottom;
        if (oldBottom < 0.99) {
          const boundaryIdx = boundaryArray.findIndex(b => Math.abs(b - oldBottom) < 0.01);
          if (boundaryIdx >= 0) newBottom = newBoundaries[boundaryIdx];
        } else {
          newBottom = 1;
        }

        panel.y = newTop;
        panel.h = newBottom - newTop;
      });
    }
  }

  // Calculate grid line segments from layout panels (supports complex layouts)
  // Layout panels define actual positions - no offsets needed since layout is modified directly
  function calculateGridLinePositions(layout, width, height) {
    const lines = [];
    const panels = layout.panels;
    const eps = 0.001; // tolerance for floating point comparison

    // Find all unique x and y positions from panel boundaries
    const xSet = new Set();
    const ySet = new Set();
    panels.forEach(p => {
      xSet.add(p.x);
      xSet.add(p.x + p.w);
      ySet.add(p.y);
      ySet.add(p.y + p.h);
    });
    const xPositions = [...xSet].sort((a, b) => a - b);
    const yPositions = [...ySet].sort((a, b) => a - b);

    // For each vertical line (x position), find where it should be drawn
    xPositions.forEach(xPos => {
      if (xPos <= eps || xPos >= 1 - eps) return; // skip edges

      // Find all y-ranges where there's a panel boundary at this x
      // We need to find the INTERSECTION of y-ranges from panels on both sides
      const segments = [];

      // Find panels on the left side of this x position (their right edge is at xPos)
      const leftPanels = panels.filter(p => Math.abs(p.x + p.w - xPos) < eps);
      // Find panels on the right side of this x position (their left edge is at xPos)
      const rightPanels = panels.filter(p => Math.abs(p.x - xPos) < eps);

      // For each left panel, find overlapping right panels and use the intersection
      leftPanels.forEach(leftPanel => {
        const leftTop = leftPanel.y;
        const leftBottom = leftPanel.y + leftPanel.h;

        rightPanels.forEach(rightPanel => {
          const rightTop = rightPanel.y;
          const rightBottom = rightPanel.y + rightPanel.h;

          // Calculate the intersection of y-ranges
          const intersectTop = Math.max(leftTop, rightTop);
          const intersectBottom = Math.min(leftBottom, rightBottom);

          // If there's a valid intersection (overlap), add it as a segment
          if (intersectBottom > intersectTop + eps) {
            segments.push({ start: intersectTop, end: intersectBottom });
          }
        });
      });

      // Merge overlapping segments
      if (segments.length > 0) {
        segments.sort((a, b) => a.start - b.start);
        const merged = [segments[0]];
        for (let i = 1; i < segments.length; i++) {
          const last = merged[merged.length - 1];
          if (segments[i].start <= last.end + eps) {
            last.end = Math.max(last.end, segments[i].end);
          } else {
            merged.push(segments[i]);
          }
        }

        // Use actual position from layout
        merged.forEach(seg => {
          lines.push({
            type: 'vertical',
            position: xPos * width,
            originalPosition: xPos, // For drag reference
            start: seg.start * height,
            end: seg.end * height,
            draggable: true
          });
        });
      }
    });

    // For each horizontal line (y position), find where it should be drawn
    yPositions.forEach(yPos => {
      if (yPos <= eps || yPos >= 1 - eps) return; // skip edges

      // Find all x-ranges where there's a panel boundary at this y
      // We need to find the INTERSECTION of x-ranges from panels above and below
      const segments = [];

      // Find panels above this y position (their bottom edge is at yPos)
      const topPanels = panels.filter(p => Math.abs(p.y + p.h - yPos) < eps);
      // Find panels below this y position (their top edge is at yPos)
      const bottomPanels = panels.filter(p => Math.abs(p.y - yPos) < eps);

      // For each top panel, find overlapping bottom panels and use the intersection
      topPanels.forEach(topPanel => {
        const topLeft = topPanel.x;
        const topRight = topPanel.x + topPanel.w;

        bottomPanels.forEach(bottomPanel => {
          const bottomLeft = bottomPanel.x;
          const bottomRight = bottomPanel.x + bottomPanel.w;

          // Calculate the intersection of x-ranges
          const intersectLeft = Math.max(topLeft, bottomLeft);
          const intersectRight = Math.min(topRight, bottomRight);

          // If there's a valid intersection (overlap), add it as a segment
          if (intersectRight > intersectLeft + eps) {
            segments.push({ start: intersectLeft, end: intersectRight });
          }
        });
      });

      // Merge overlapping segments
      if (segments.length > 0) {
        segments.sort((a, b) => a.start - b.start);
        const merged = [segments[0]];
        for (let i = 1; i < segments.length; i++) {
          const last = merged[merged.length - 1];
          if (segments[i].start <= last.end + eps) {
            last.end = Math.max(last.end, segments[i].end);
          } else {
            merged.push(segments[i]);
          }
        }

        // Use actual position from layout
        merged.forEach(seg => {
          lines.push({
            type: 'horizontal',
            position: yPos * height,
            originalPosition: yPos, // For drag reference
            start: seg.start * width,
            end: seg.end * width,
            draggable: true
          });
        });
      }
    });

    return lines;
  }

  // ============================================
  // Bind Banner Row Events (Event Delegation)
  // ============================================
  function bindBannerRowEvents() {
    const container = document.getElementById('bannerScrollContainer');

    // Panel click (file selection)
    container.addEventListener('click', (e) => {
      // Delete button (banner row)
      const deleteBtn = e.target.closest('.banner-delete-btn');
      if (deleteBtn) {
        const index = parseInt(deleteBtn.dataset.bannerIndex);
        deleteBanner(index);
        return;
      }

      // Panel image delete button
      const panelDeleteBtn = e.target.closest('.v0-banner-creator__panel-delete');
      if (panelDeleteBtn) {
        const panelIndex = parseInt(panelDeleteBtn.dataset.panelIndex);
        const bannerIndex = parseInt(panelDeleteBtn.dataset.bannerIndex);
        const banner = state.banners[bannerIndex];

        // Clear the panel image/video
        banner.panels[panelIndex] = { image: null, isVideo: false, videoElement: null, x: 0, y: 0, scale: 1, naturalWidth: 0, naturalHeight: 0 };

        // Re-render
        const row = container.querySelector(`.banner-lab-v4__banner-row[data-banner-index="${bannerIndex}"]`);
        const panelsContainer = row.querySelector('.banner-lab-v4__panels-container');
        renderCanvasPanels(banner, bannerIndex, panelsContainer);
        updateMockupPreview(bannerIndex, row);
        updateFormatButtons(bannerIndex, row);
        return;
      }

      // Panel replace button - triggers file input
      const panelReplaceBtn = e.target.closest('.v0-banner-creator__panel-replace');
      if (panelReplaceBtn) {
        const panelIndex = parseInt(panelReplaceBtn.dataset.panelIndex);
        const bannerIndex = parseInt(panelReplaceBtn.dataset.bannerIndex);
        const row = container.querySelector(`.banner-lab-v4__banner-row[data-banner-index="${bannerIndex}"]`);
        const panelsContainer = row.querySelector('.banner-lab-v4__panels-container');
        const panel = panelsContainer.querySelector(`.v0-banner-creator__panel[data-panel-index="${panelIndex}"]`);
        const fileInput = panel.querySelector('.v0-banner-creator__file-input');
        if (fileInput) fileInput.click();
        return;
      }

      // Mockup download
      const mockupDownloadBtn = e.target.closest('.banner-lab-v4__mockup-download');
      if (mockupDownloadBtn) {
        const index = parseInt(mockupDownloadBtn.dataset.bannerIndex);
        downloadMockup(index);
        return;
      }

      // Export button
      const exportBtn = e.target.closest('.banner-lab-v4__export-btn');
      if (exportBtn) {
        const index = parseInt(exportBtn.dataset.bannerIndex);
        downloadBanner(index);
        return;
      }

      // Platform tabs
      const platformTab = e.target.closest('.mockup-tab');
      if (platformTab) {
        const bannerIndex = parseInt(platformTab.dataset.bannerIndex);
        const platform = platformTab.dataset.platform;
        const row = platformTab.closest('.banner-lab-v4__banner-row');

        // Update tab active state
        row.querySelectorAll('.mockup-tab').forEach(t => t.classList.remove('mockup-tab--active'));
        platformTab.classList.add('mockup-tab--active');

        // Update banner state
        state.banners[bannerIndex].currentPlatform = platform;

        // Toggle mockup visibility
        const isReels = state.banners[bannerIndex].aspectRatio === '9:16';
        row.querySelector('[data-mockup-type="ig-feed"]').style.display = (platform === 'instagram' && !isReels) ? '' : 'none';
        row.querySelector('[data-mockup-type="ig-reels"]').style.display = (platform === 'instagram' && isReels) ? '' : 'none';
        row.querySelector('[data-mockup-type="tiktok"]').style.display = platform === 'tiktok' ? '' : 'none';

        return;
      }

      // Device tabs (Apple, Google, Samsung, Huawei, Generic)
      const deviceTab = e.target.closest('.device-tab');
      if (deviceTab) {
        const bannerIndex = parseInt(deviceTab.dataset.bannerIndex);
        const device = deviceTab.dataset.device;
        const row = deviceTab.closest('.banner-lab-v4__banner-row');

        // Update tab active state
        row.querySelectorAll('.device-tab').forEach(t => t.classList.remove('device-tab--active'));
        deviceTab.classList.add('device-tab--active');

        // Update banner state
        state.banners[bannerIndex].currentDevice = device;

        // Apply device-specific styles to phone frame (keep aspect class!)
        const phoneFrame = row.querySelector('.banner-lab-v4__phone-frame');
        const banner = state.banners[bannerIndex];
        const aspectClass = 'aspect-' + banner.aspectRatio.replace(':', '-');

        // Remove only device classes, keep aspect class
        phoneFrame.classList.remove('device-apple', 'device-google', 'device-samsung', 'device-huawei', 'device-generic');
        phoneFrame.classList.add(`device-${device}`);

        // Use real mockup images for Apple, Google, Samsung; CSS-drawn for others
        const hasRealMockup = ['apple', 'google'].includes(device);  // Samsung uses CSS-drawn frame
        phoneFrame.classList.toggle('use-real-mockup', hasRealMockup);

        return;
      }

      // Ratio buttons
      const ratioBtn = e.target.closest('.ratio-btn');
      if (ratioBtn) {
        const bannerIndex = parseInt(ratioBtn.dataset.bannerIndex);
        const row = ratioBtn.closest('.banner-lab-v4__banner-row');

        row.querySelectorAll('.ratio-btn').forEach(b => b.classList.remove('ratio-btn--active'));
        ratioBtn.classList.add('ratio-btn--active');

        const width = parseInt(ratioBtn.dataset.width);
        const height = parseInt(ratioBtn.dataset.height);

        const banner = state.banners[bannerIndex];
        banner.outputWidth = width;
        banner.outputHeight = height;

        // Determine aspect ratio
        if (width === 1920 && height === 1080) banner.aspectRatio = '16:9';
        else if (width === 1080 && height === 1920) banner.aspectRatio = '9:16';
        else if (width === 1080 && height === 1080) banner.aspectRatio = '1:1';
        else if (width === 1080 && height === 1350) banner.aspectRatio = '4:5';
        else banner.aspectRatio = 'wide';

        // Update size display
        row.querySelector('.size-value').textContent = `${width} × ${height}`;

        // Toggle mockup based on aspect ratio
        const isReels = banner.aspectRatio === '9:16';
        const platform = banner.currentPlatform;
        row.querySelector('[data-mockup-type="ig-feed"]').style.display = (platform === 'instagram' && !isReels) ? '' : 'none';
        row.querySelector('[data-mockup-type="ig-reels"]').style.display = (platform === 'instagram' && isReels) ? '' : 'none';

        // Update phone frame aspect ratio class
        const phoneFrame = row.querySelector('.banner-lab-v4__phone-frame');
        phoneFrame.classList.remove('aspect-16-9', 'aspect-9-16', 'aspect-1-1', 'aspect-4-5', 'aspect-wide');
        const aspectClass = 'aspect-' + banner.aspectRatio.replace(':', '-');
        phoneFrame.classList.add(aspectClass);

        // Re-render canvas
        const panelsContainer = row.querySelector('.banner-lab-v4__panels-container');
        renderCanvasPanels(banner, bannerIndex, panelsContainer);
        updateMockupPreview(bannerIndex, row);
        return;
      }

      // Grid buttons
      const gridBtn = e.target.closest('.grid-btn');
      if (gridBtn) {
        const bannerIndex = parseInt(gridBtn.dataset.bannerIndex);
        const row = gridBtn.closest('.banner-lab-v4__banner-row');

        row.querySelectorAll('.grid-btn').forEach(b => b.classList.remove('grid-btn--active'));
        gridBtn.classList.add('grid-btn--active');

        const panelCount = parseInt(gridBtn.dataset.panels);
        const banner = state.banners[bannerIndex];
        const oldPanels = banner.panels.slice(); // Keep old panels

        banner.panelCount = panelCount;
        // Deep copy to allow per-banner layout modifications
        banner.selectedLayout = JSON.parse(JSON.stringify(LAYOUTS[panelCount][0]));

        // Preserve existing images/videos: expand or shrink panel array while keeping media
        const newPanels = [];
        for (let i = 0; i < panelCount; i++) {
          if (oldPanels[i] && oldPanels[i].image) {
            // Recalculate position/scale for new panel dimensions
            const panelDef = banner.selectedLayout.panels[i];
            const panelWidth = panelDef.w * banner.outputWidth;
            const panelHeight = panelDef.h * banner.outputHeight;

            const oldData = oldPanels[i];
            const scaleX = panelWidth / oldData.naturalWidth;
            const scaleY = panelHeight / oldData.naturalHeight;
            const newScale = Math.max(scaleX, scaleY);

            const scaledWidth = oldData.naturalWidth * newScale;
            const scaledHeight = oldData.naturalHeight * newScale;
            const newX = (panelWidth - scaledWidth) / 2;
            const newY = (panelHeight - scaledHeight) / 2;

            newPanels.push({
              image: oldData.image,
              isVideo: oldData.isVideo || false,
              videoElement: oldData.videoElement || null,
              x: newX,
              y: newY,
              scale: newScale,
              naturalWidth: oldData.naturalWidth,
              naturalHeight: oldData.naturalHeight
            });
          } else {
            newPanels.push({ image: null, isVideo: false, videoElement: null, x: 0, y: 0, scale: 1, naturalWidth: 0, naturalHeight: 0 });
          }
        }
        banner.panels = newPanels;

        // Show/hide pattern group
        const patternGroup = row.querySelector('.layout-pattern-group');
        patternGroup.style.display = panelCount > 1 ? '' : 'none';

        // Show/hide grid line group (only for 2+ panels)
        const gridLineGroup = row.querySelector('.grid-line-group');
        gridLineGroup.style.display = panelCount > 1 ? '' : 'none';

        // Auto-enable grid lines when selecting 2+ panels (default: ON with 2px white)
        if (panelCount > 1) {
          banner.gridLineEnabled = true;
          banner.gridLineColor = banner.gridLineColor || '#ffffff';
          banner.gridLineWidth = banner.gridLineWidth || 2;
          // Update toggle button UI
          const toggleBtn = row.querySelector('.grid-line-toggle-btn');
          if (toggleBtn) toggleBtn.classList.add('active');
        } else {
          banner.gridLineEnabled = false;
          const toggleBtn = row.querySelector('.grid-line-toggle-btn');
          if (toggleBtn) toggleBtn.classList.remove('active');
        }

        // Render pattern buttons
        if (panelCount > 1) {
          renderPatternButtons(banner, bannerIndex, row);
        }

        // Re-render canvas
        const panelsContainer = row.querySelector('.banner-lab-v4__panels-container');
        renderCanvasPanels(banner, bannerIndex, panelsContainer);
        updateMockupPreview(bannerIndex, row);
        return;
      }

      // Pattern buttons
      const patternBtn = e.target.closest('.pattern-btn');
      if (patternBtn) {
        const bannerIndex = parseInt(patternBtn.dataset.bannerIndex);
        const layoutIndex = parseInt(patternBtn.dataset.layoutIndex);
        const row = patternBtn.closest('.banner-lab-v4__banner-row');

        row.querySelectorAll('.pattern-btn').forEach(b => b.classList.remove('pattern-btn--active'));
        patternBtn.classList.add('pattern-btn--active');

        const banner = state.banners[bannerIndex];
        // Deep copy to allow per-banner layout modifications
        banner.selectedLayout = JSON.parse(JSON.stringify(LAYOUTS[banner.panelCount][layoutIndex]));

        // Re-render canvas
        const panelsContainer = row.querySelector('.banner-lab-v4__panels-container');
        renderCanvasPanels(banner, bannerIndex, panelsContainer);
        updateMockupPreview(bannerIndex, row);
        return;
      }

      // Format buttons
      const formatBtn = e.target.closest('.format-btn');
      if (formatBtn) {
        const bannerIndex = parseInt(formatBtn.dataset.bannerIndex);
        const row = formatBtn.closest('.banner-lab-v4__banner-row');

        row.querySelectorAll('.format-btn').forEach(b => b.classList.remove('format-btn--active'));
        formatBtn.classList.add('format-btn--active');

        state.banners[bannerIndex].format = formatBtn.dataset.format;
        return;
      }

      // Panel click
      const panel = e.target.closest('.v0-banner-creator__panel');
      if (panel) {
        const img = panel.querySelector('.v0-banner-creator__panel-image');
        if (img && img.contains(e.target)) return;

        const fileInput = panel.querySelector('.v0-banner-creator__file-input');
        if (fileInput) fileInput.click();
      }
    });

    // Texture buttons and Grid line toggle
    container.addEventListener('click', (e) => {
      // Texture button (Digital/Film/Paper)
      if (e.target.closest('.texture-btn')) {
        const btn = e.target.closest('.texture-btn');
        const bannerIndex = parseInt(btn.dataset.bannerIndex);
        const textureType = btn.dataset.texture;
        const banner = state.banners[bannerIndex];
        const row = container.querySelector(`.banner-lab-v4__banner-row[data-banner-index="${bannerIndex}"]`);

        // Get all texture buttons in this row
        const allTextureBtns = row.querySelectorAll('.texture-btn');
        const intensityWrapper = row.querySelector('.texture-intensity-wrapper');

        // Toggle: if same button clicked, turn off; otherwise switch to new texture
        if (banner.textureType === textureType) {
          // Turn off
          banner.textureType = null;
          allTextureBtns.forEach(b => b.classList.remove('active'));
          intensityWrapper.style.display = 'none';
        } else {
          // Switch to new texture
          banner.textureType = textureType;
          allTextureBtns.forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          intensityWrapper.style.display = 'flex';
        }

        // Update texture preview overlay on canvas
        updateTexturePreview(bannerIndex, row, banner.textureType, banner.textureIntensity);
        // Update mockup preview to reflect texture change
        updateMockupPreview(bannerIndex, row);
        return;
      }

      // Grid line toggle button
      if (e.target.closest('.grid-line-toggle-btn')) {
        const btn = e.target.closest('.grid-line-toggle-btn');
        const bannerIndex = parseInt(btn.dataset.bannerIndex);
        const banner = state.banners[bannerIndex];
        banner.gridLineEnabled = !banner.gridLineEnabled;
        btn.classList.toggle('active', banner.gridLineEnabled);

        const row = container.querySelector(`.banner-lab-v4__banner-row[data-banner-index="${bannerIndex}"]`);
        const panelsContainer = row.querySelector('.banner-lab-v4__panels-container');
        renderCanvasPanels(banner, bannerIndex, panelsContainer);
        updateMockupPreview(bannerIndex, row);
        return;
      }

      // Color dot click
      if (e.target.classList.contains('grid-line-dot')) {
        const dot = e.target;
        const dotsContainer = dot.closest('.grid-line-color-dots');
        const bannerIndex = parseInt(dotsContainer.dataset.bannerIndex);
        const banner = state.banners[bannerIndex];

        // Update active state
        dotsContainer.querySelectorAll('.grid-line-dot').forEach(d => d.classList.remove('active'));
        dot.classList.add('active');

        banner.gridLineColor = dot.dataset.color;

        // Auto-enable grid line when color is selected
        if (!banner.gridLineEnabled) {
          banner.gridLineEnabled = true;
          const toggleBtn = container.querySelector(`.grid-line-toggle-btn[data-banner-index="${bannerIndex}"]`);
          if (toggleBtn) toggleBtn.classList.add('active');
        }

        const row = container.querySelector(`.banner-lab-v4__banner-row[data-banner-index="${bannerIndex}"]`);
        const panelsContainer = row.querySelector('.banner-lab-v4__panels-container');
        renderCanvasPanels(banner, bannerIndex, panelsContainer);
        updateMockupPreview(bannerIndex, row);
        return;
      }

      // Grid line reset button
      if (e.target.closest('.grid-line-reset-btn')) {
        const btn = e.target.closest('.grid-line-reset-btn');
        const bannerIndex = parseInt(btn.dataset.bannerIndex);
        const banner = state.banners[bannerIndex];

        // Reset custom offsets
        banner.gridLineOffsets = {};

        // Reset layout to default for current panel count
        const defaultLayouts = LAYOUTS[banner.panelCount];
        if (defaultLayouts && defaultLayouts.length > 0) {
          // Deep copy to avoid mutating the original LAYOUTS
          const defaultLayout = JSON.parse(JSON.stringify(defaultLayouts[0]));
          banner.selectedLayout = defaultLayout;
        }

        // Re-render
        const row = container.querySelector(`.banner-lab-v4__banner-row[data-banner-index="${bannerIndex}"]`);
        const panelsContainer = row.querySelector('.banner-lab-v4__panels-container');
        renderCanvasPanels(banner, bannerIndex, panelsContainer);
        updateMockupPreview(bannerIndex, row);
        return;
      }
    });

    // Grid line width slider and texture intensity slider
    container.addEventListener('input', (e) => {
      // Texture intensity slider
      if (e.target.classList.contains('texture-intensity-slider')) {
        const slider = e.target;
        const bannerIndex = parseInt(slider.dataset.bannerIndex);
        const banner = state.banners[bannerIndex];
        banner.textureIntensity = parseInt(slider.value) / 100; // Convert 10-100 to 0.1-1.0

        // Update display
        const valueDisplay = slider.nextElementSibling;
        if (valueDisplay) valueDisplay.textContent = slider.value + '%';

        // Update texture preview with new intensity
        const row = container.querySelector(`.banner-lab-v4__banner-row[data-banner-index="${bannerIndex}"]`);
        if (row && banner.textureType) {
          updateTexturePreview(bannerIndex, row, banner.textureType, banner.textureIntensity);
          // Update mockup preview to reflect intensity change
          updateMockupPreview(bannerIndex, row);
        }
        return;
      }

      // Grid line width slider
      if (e.target.classList.contains('grid-line-width-slider')) {
        const slider = e.target;
        const bannerIndex = parseInt(slider.dataset.bannerIndex);
        const banner = state.banners[bannerIndex];
        banner.gridLineWidth = parseInt(slider.value);

        // Update display
        const valueDisplay = slider.nextElementSibling;
        if (valueDisplay) valueDisplay.textContent = slider.value + 'px';

        // Auto-enable grid line when width is changed
        if (!banner.gridLineEnabled) {
          banner.gridLineEnabled = true;
          const toggleBtn = container.querySelector(`.grid-line-toggle-btn[data-banner-index="${bannerIndex}"]`);
          if (toggleBtn) toggleBtn.classList.add('active');
        }

        const row = container.querySelector(`.banner-lab-v4__banner-row[data-banner-index="${bannerIndex}"]`);
        const panelsContainer = row.querySelector('.banner-lab-v4__panels-container');
        renderCanvasPanels(banner, bannerIndex, panelsContainer);
        updateMockupPreview(bannerIndex, row);
        return;
      }
    });

    // File selection
    container.addEventListener('change', (e) => {
      if (!e.target.classList.contains('v0-banner-creator__file-input')) return;

      const file = e.target.files[0];
      if (!file) return;

      const panelIndex = parseInt(e.target.dataset.panelIndex);
      const bannerIndex = parseInt(e.target.dataset.bannerIndex);

      loadImage(file, bannerIndex, panelIndex);
    });

    // Drag handling
    let isDragging = false;
    let activePanelIndex = null;
    let activeBannerIndex = null;
    let dragStart = { x: 0, y: 0 };

    container.addEventListener('mousedown', (e) => {
      const img = e.target.closest('.v0-banner-creator__panel-image');
      if (!img) return;

      e.preventDefault();
      const panel = img.closest('.v0-banner-creator__panel');

      activePanelIndex = parseInt(panel.dataset.panelIndex);
      activeBannerIndex = parseInt(panel.dataset.bannerIndex);
      isDragging = true;
      dragStart = { x: e.clientX, y: e.clientY };

      img.style.cursor = 'grabbing';
    });

    document.addEventListener('mousemove', (e) => {
      if (!isDragging || activePanelIndex === null) return;

      const banner = state.banners[activeBannerIndex];
      // Calculate displayScale same way as renderCanvasPanels
      const wrapperWidth = 460;
      const wrapperHeight = 420;
      const outputAspect = banner.outputWidth / banner.outputHeight;
      const containerAspect = wrapperWidth / wrapperHeight;
      let displayWidth = outputAspect > containerAspect ? wrapperWidth : wrapperHeight * outputAspect;
      const displayScale = displayWidth / banner.outputWidth;

      const dx = (e.clientX - dragStart.x) / displayScale;
      const dy = (e.clientY - dragStart.y) / displayScale;

      banner.panels[activePanelIndex].x += dx;
      banner.panels[activePanelIndex].y += dy;

      dragStart = { x: e.clientX, y: e.clientY };

      // Update UI (lightweight - no DOM rebuild to avoid flicker)
      const row = container.querySelector(`.banner-lab-v4__banner-row[data-banner-index="${activeBannerIndex}"]`);
      const panelsContainer = row.querySelector('.banner-lab-v4__panels-container');
      updatePanelMediaPosition(banner, activeBannerIndex, activePanelIndex, panelsContainer);
      updateMockupPreview(activeBannerIndex, row);
    });

    document.addEventListener('mouseup', () => {
      isDragging = false;
      activePanelIndex = null;
      activeBannerIndex = null;
    });

    // Scroll to zoom
    container.addEventListener('wheel', (e) => {
      const panel = e.target.closest('.v0-banner-creator__panel');
      if (!panel) return;

      const panelIndex = parseInt(panel.dataset.panelIndex);
      const bannerIndex = parseInt(panel.dataset.bannerIndex);
      const banner = state.banners[bannerIndex];
      const panelData = banner.panels[panelIndex];

      if (!panelData || !panelData.image) return;

      e.preventDefault();

      const delta = e.deltaY > 0 ? 0.95 : 1.05;
      panelData.scale = Math.max(0.1, Math.min(5, panelData.scale * delta));

      // Update UI (lightweight - no DOM rebuild to avoid flicker)
      const row = container.querySelector(`.banner-lab-v4__banner-row[data-banner-index="${bannerIndex}"]`);
      const panelsContainer = row.querySelector('.banner-lab-v4__panels-container');
      updatePanelMediaPosition(banner, bannerIndex, panelIndex, panelsContainer);
      updateMockupPreview(bannerIndex, row);
    }, { passive: false });

    // Drag and drop file upload
    container.addEventListener('dragover', (e) => {
      const panel = e.target.closest('.v0-banner-creator__panel');
      if (!panel) return;

      e.preventDefault();
      e.stopPropagation();
      panel.classList.add('v0-banner-creator__panel--dragover');
    });

    container.addEventListener('dragleave', (e) => {
      const panel = e.target.closest('.v0-banner-creator__panel');
      if (!panel) return;

      e.preventDefault();
      e.stopPropagation();
      panel.classList.remove('v0-banner-creator__panel--dragover');
    });

    container.addEventListener('drop', (e) => {
      const panel = e.target.closest('.v0-banner-creator__panel');
      if (!panel) return;

      e.preventDefault();
      e.stopPropagation();
      panel.classList.remove('v0-banner-creator__panel--dragover');

      const files = e.dataTransfer.files;
      if (files.length === 0) return;

      const file = files[0];
      // Accept both images and videos
      if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) return;

      const panelIndex = parseInt(panel.dataset.panelIndex);
      const bannerIndex = parseInt(panel.dataset.bannerIndex);

      loadImage(file, bannerIndex, panelIndex);
    });
  }

  // ============================================
  // Render Pattern Buttons
  // ============================================
  function renderPatternButtons(banner, bannerIndex, row) {
    const container = row.querySelector(`.pattern-buttons[data-banner-index="${bannerIndex}"]`);
    container.innerHTML = '';

    const layouts = LAYOUTS[banner.panelCount];
    layouts.forEach((layout, index) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pattern-btn' + (index === 0 ? ' pattern-btn--active' : '');
      btn.dataset.layoutIndex = index;
      btn.dataset.bannerIndex = bannerIndex;

      const preview = document.createElement('div');
      preview.className = 'pattern-preview' + (layout.type === 'vertical' ? ' pattern-preview--vertical' : '');

      layout.panels.forEach(p => {
        const cell = document.createElement('div');
        cell.className = 'pattern-cell' + (p.w > 0.5 || p.h > 0.5 ? ' pattern-cell--large' : '');
        preview.appendChild(cell);
      });

      btn.appendChild(preview);
      container.appendChild(btn);
    });
  }

  // ============================================
  // Load Image or Video
  // ============================================
  function loadImage(file, bannerIndex, panelIndex) {
    if (DEBUG) console.log('[Banner Lab] loadImage called:', file.name, file.type, 'banner:', bannerIndex, 'panel:', panelIndex);
    const isVideo = file.type.startsWith('video/');

    if (isVideo) {
      // Check if there's already a video in ANY banner (limit 1 video total)
      let existingVideoBannerIndex = -1;
      let existingVideoPanelIndex = -1;
      for (let bi = 0; bi < state.banners.length; bi++) {
        const b = state.banners[bi];
        for (let pi = 0; pi < b.panels.length; pi++) {
          if (b.panels[pi] && b.panels[pi].isVideo) {
            // Allow replacing video in the same panel
            if (bi === bannerIndex && pi === panelIndex) continue;
            existingVideoBannerIndex = bi;
            existingVideoPanelIndex = pi;
            break;
          }
        }
        if (existingVideoBannerIndex !== -1) break;
      }

      if (existingVideoBannerIndex !== -1) {
        alert('動画は全体で1つまでです。パフォーマンスの都合上、複数の動画はサポートしていません。\n\nOnly 1 video allowed. Multiple videos are not supported for performance reasons.');
        if (DEBUG) console.log('[Banner Lab] Video upload blocked - already have video at banner:', existingVideoBannerIndex, 'panel:', existingVideoPanelIndex);
        return;
      }

      // Handle video
      if (DEBUG) console.log('[Banner Lab] Detected video, calling loadVideo');
      loadVideo(file, bannerIndex, panelIndex);
    } else {
      // Handle image
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const banner = state.banners[bannerIndex];
          const panelDef = banner.selectedLayout.panels[panelIndex];
          const panelWidth = panelDef.w * banner.outputWidth;
          const panelHeight = panelDef.h * banner.outputHeight;

          const scaleX = panelWidth / img.naturalWidth;
          const scaleY = panelHeight / img.naturalHeight;
          const scale = Math.max(scaleX, scaleY);

          const scaledWidth = img.naturalWidth * scale;
          const scaledHeight = img.naturalHeight * scale;
          const x = (panelWidth - scaledWidth) / 2;
          const y = (panelHeight - scaledHeight) / 2;

          banner.panels[panelIndex] = {
            image: e.target.result,
            isVideo: false,
            videoElement: null,
            x: x,
            y: y,
            scale: scale,
            naturalWidth: img.naturalWidth,
            naturalHeight: img.naturalHeight
          };

          // Update UI
          const row = document.querySelector(`.banner-lab-v4__banner-row[data-banner-index="${bannerIndex}"]`);
          const panelsContainer = row.querySelector('.banner-lab-v4__panels-container');
          renderCanvasPanels(banner, bannerIndex, panelsContainer);
          updateMockupPreview(bannerIndex, row);
          updateFormatButtons(bannerIndex, row);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  // ============================================
  // Load Video
  // ============================================
  function loadVideo(file, bannerIndex, panelIndex) {
    if (DEBUG) console.log('[Banner Lab] loadVideo called:', file.name);
    const videoUrl = URL.createObjectURL(file);
    if (DEBUG) console.log('[Banner Lab] Video blob URL created:', videoUrl);

    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.preload = 'auto';
    video.style.cssText = 'position:fixed;top:0;left:0;width:320px;height:180px;opacity:0.01;pointer-events:none;z-index:-1;';
    document.body.appendChild(video);

    let processed = false;

    const processVideoWithDimensions = (width, height) => {
      if (processed) return;
      processed = true;
      video.pause();

      if (DEBUG) console.log('[Banner Lab] Processing video:', width, 'x', height);
      const banner = state.banners[bannerIndex];
      const panelDef = banner.selectedLayout.panels[panelIndex];
      const panelWidth = panelDef.w * banner.outputWidth;
      const panelHeight = panelDef.h * banner.outputHeight;

      const scaleX = panelWidth / width;
      const scaleY = panelHeight / height;
      const scale = Math.max(scaleX, scaleY);

      const scaledWidth = width * scale;
      const scaledHeight = height * scale;
      const x = (panelWidth - scaledWidth) / 2;
      const y = (panelHeight - scaledHeight) / 2;

      banner.panels[panelIndex] = {
        image: videoUrl,
        isVideo: true,
        videoElement: video,
        x: x,
        y: y,
        scale: scale,
        naturalWidth: width,
        naturalHeight: height
      };

      if (DEBUG) console.log('[Banner Lab] Video panel data set');

      const row = document.querySelector(`.banner-lab-v4__banner-row[data-banner-index="${bannerIndex}"]`);
      const panelsContainer = row.querySelector('.banner-lab-v4__panels-container');
      renderCanvasPanels(banner, bannerIndex, panelsContainer);
      updateMockupPreview(bannerIndex, row);
      updateFormatButtons(bannerIndex, row);
      if (DEBUG) console.log('[Banner Lab] Video UI updated');
    };

    const tryProcessVideo = () => {
      if (processed) return;
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        processVideoWithDimensions(video.videoWidth, video.videoHeight);
      }
    };

    video.onerror = (e) => {
      console.error('[Banner Lab] Video error:', video.error?.message || e);
      // Fallback: assume 9:16 vertical video
      if (!processed) {
        if (DEBUG) console.log('[Banner Lab] Using fallback dimensions (1080x1920)');
        processVideoWithDimensions(1080, 1920);
      }
    };

    video.onloadedmetadata = () => {
      if (DEBUG) console.log('[Banner Lab] Metadata loaded:', video.videoWidth, 'x', video.videoHeight);
      tryProcessVideo();
    };

    video.oncanplay = () => {
      if (DEBUG) console.log('[Banner Lab] Can play');
      tryProcessVideo();
    };

    video.onloadeddata = () => {
      if (DEBUG) console.log('[Banner Lab] Data loaded');
      tryProcessVideo();
    };

    video.src = videoUrl;
    video.load();

    // Try to play
    try {
      const playPromise = video.play();
      if (playPromise) {
        playPromise.then(() => {
          if (DEBUG) console.log('[Banner Lab] Play started');
          tryProcessVideo();
        }).catch(err => {
          if (DEBUG) console.log('[Banner Lab] Autoplay blocked:', err.message);
        });
      }
    } catch (e) {
      if (DEBUG) console.log('[Banner Lab] Play error:', e.message);
    }

    // Multiple fallback checks with final fallback to default dimensions
    [100, 300, 600, 1000].forEach(delay => {
      setTimeout(() => {
        if (!processed && video.videoWidth > 0) {
          if (DEBUG) console.log('[Banner Lab] Fallback @' + delay + 'ms:', video.videoWidth, 'x', video.videoHeight);
          tryProcessVideo();
        }
      }, delay);
    });

    // Final fallback: use default dimensions after 2 seconds
    setTimeout(() => {
      if (!processed) {
        if (DEBUG) console.log('[Banner Lab] Final fallback: using default dimensions');
        processVideoWithDimensions(1080, 1920);
      }
    }, 2000);
  }

  // ============================================
  // Update Mockup Preview (with debounce for performance)
  // ============================================
  function updateMockupPreview(bannerIndex, row) {
    // Debounce: cancel any pending update for this banner
    if (mockupPreviewTimers[bannerIndex]) {
      clearTimeout(mockupPreviewTimers[bannerIndex]);
    }

    // Delay the actual update by 100ms
    mockupPreviewTimers[bannerIndex] = setTimeout(() => {
      updateMockupPreviewImmediate(bannerIndex, row);
    }, 100);
  }

  // Actual mockup preview update (called after debounce)
  function updateMockupPreviewImmediate(bannerIndex, row) {
    const banner = state.banners[bannerIndex];
    const hasImages = banner.panels.some(p => p && p.image);
    const hasVideo = banner.panels.some(p => p && p.isVideo);

    const mockupContainers = row.querySelectorAll('.mockup-image-container');

    if (!hasImages) {
      mockupContainers.forEach(container => {
        const placeholder = container.querySelector('.mockup-placeholder');
        if (placeholder) placeholder.style.display = '';
        const canvas = container.querySelector('canvas');
        if (canvas) canvas.remove();
        const videoWrapper = container.querySelector('.mockup-video-wrapper');
        if (videoWrapper) videoWrapper.remove();
      });
      return;
    }

    // If has video, use video element for live playback in mockup
    if (hasVideo) {
      mockupContainers.forEach(container => {
        const placeholder = container.querySelector('.mockup-placeholder');
        if (placeholder) placeholder.style.display = 'none';

        // Remove any existing canvas
        const existingCanvas = container.querySelector('canvas');
        if (existingCanvas) existingCanvas.remove();

        // Create or get video wrapper
        let videoWrapper = container.querySelector('.mockup-video-wrapper');
        if (!videoWrapper) {
          videoWrapper = document.createElement('div');
          videoWrapper.className = 'mockup-video-wrapper';
          videoWrapper.style.cssText = 'position: absolute; top: 0; left: 0; width: 100%; height: 100%; overflow: hidden !important; -webkit-mask-image: -webkit-radial-gradient(white, black); mask-image: radial-gradient(white, black);';
          container.appendChild(videoWrapper);
        }
        videoWrapper.innerHTML = '';

        // Get container dimensions
        const containerRect = container.getBoundingClientRect();
        const containerWidth = containerRect.width || 200;
        const containerHeight = containerRect.height || 350;

        // Calculate scale to fit output to container (maintain aspect ratio)
        const scaleX = containerWidth / banner.outputWidth;
        const scaleY = containerHeight / banner.outputHeight;
        const mockupScale = Math.min(scaleX, scaleY);

        // Calculate scale to fill container edge-to-edge (like real iPhone Reels)
        const scaledWidth = banner.outputWidth * mockupScale;
        const scaledHeight = banner.outputHeight * mockupScale;
        // Content starts at top: 0 to fill behind Dynamic Island (no vertical offset)
        const offsetX = (containerWidth - scaledWidth) / 2;
        const offsetY = 0; // Edge-to-edge: content starts at top

        // Create a container for all scaled content (top-aligned, centered horizontally)
        const scaledContainer = document.createElement('div');
        scaledContainer.style.cssText = `position: absolute; left: ${offsetX}px; top: ${offsetY}px; width: ${scaledWidth}px; height: ${scaledHeight}px; overflow: hidden !important; -webkit-mask-image: -webkit-radial-gradient(white, black); mask-image: radial-gradient(white, black);`;

        // Render each panel as video or image
        banner.selectedLayout.panels.forEach((panelDef, index) => {
          const panelData = banner.panels[index];
          if (!panelData || !panelData.image) return;

          const px = panelDef.x * banner.outputWidth * mockupScale;
          const py = panelDef.y * banner.outputHeight * mockupScale;
          const pw = panelDef.w * banner.outputWidth * mockupScale;
          const ph = panelDef.h * banner.outputHeight * mockupScale;

          const panelEl = document.createElement('div');
          panelEl.style.cssText = `position: absolute; left: ${px}px; top: ${py}px; width: ${pw}px; height: ${ph}px; overflow: hidden !important; -webkit-mask-image: -webkit-radial-gradient(white, black); mask-image: radial-gradient(white, black);`;

          if (panelData.isVideo) {
            // Create a new video element for mockup preview
            const video = document.createElement('video');
            video.src = panelData.image;
            video.muted = true;
            video.loop = true;
            video.playsInline = true;
            video.autoplay = true; // Auto-play for mockup preview

            const imgWidth = panelData.naturalWidth * panelData.scale * mockupScale;
            const imgHeight = panelData.naturalHeight * panelData.scale * mockupScale;
            const imgX = panelData.x * mockupScale;
            const imgY = panelData.y * mockupScale;

            video.style.cssText = `position: absolute; left: ${imgX}px; top: ${imgY}px; width: ${imgWidth}px; height: ${imgHeight}px; object-fit: cover;`;
            panelEl.appendChild(video);

            // Try to sync with source video if available
            const sourceVideo = panelData.videoElement;
            if (sourceVideo) {
              video.currentTime = sourceVideo.currentTime;
              // Keep videos synchronized
              sourceVideo.addEventListener('play', () => video.play().catch(() => {}));
              sourceVideo.addEventListener('pause', () => video.pause());
              sourceVideo.addEventListener('seeked', () => { video.currentTime = sourceVideo.currentTime; });
            }

            // Always try to play the video
            video.play().catch(() => {});
          } else {
            const img = document.createElement('img');
            img.src = panelData.image;

            const imgWidth = panelData.naturalWidth * panelData.scale * mockupScale;
            const imgHeight = panelData.naturalHeight * panelData.scale * mockupScale;
            const imgX = panelData.x * mockupScale;
            const imgY = panelData.y * mockupScale;

            img.style.cssText = `position: absolute; left: ${imgX}px; top: ${imgY}px; width: ${imgWidth}px; height: ${imgHeight}px; object-fit: cover;`;
            panelEl.appendChild(img);
          }

          scaledContainer.appendChild(panelEl);
        });

        // Add grid lines if enabled (inside scaledContainer)
        if (banner.gridLineEnabled && banner.panelCount > 1) {
          const linePositions = calculateGridLinePositions(banner.selectedLayout, banner.outputWidth * mockupScale, banner.outputHeight * mockupScale);
          linePositions.forEach(line => {
            const lineEl = document.createElement('div');
            lineEl.style.position = 'absolute';
            lineEl.style.backgroundColor = banner.gridLineColor;

            if (line.type === 'vertical') {
              lineEl.style.left = (line.position - banner.gridLineWidth * mockupScale / 2) + 'px';
              lineEl.style.top = line.start + 'px';
              lineEl.style.width = (banner.gridLineWidth * mockupScale) + 'px';
              lineEl.style.height = (line.end - line.start) + 'px';
            } else {
              lineEl.style.left = line.start + 'px';
              lineEl.style.top = (line.position - banner.gridLineWidth * mockupScale / 2) + 'px';
              lineEl.style.width = (line.end - line.start) + 'px';
              lineEl.style.height = (banner.gridLineWidth * mockupScale) + 'px';
            }
            scaledContainer.appendChild(lineEl);
          });
        }

        // Add texture overlay to scaledContainer if enabled
        if (banner.textureType) {
          const textureOverlay = document.createElement('div');
          textureOverlay.className = `texture-preview-overlay texture-${banner.textureType}`;
          textureOverlay.style.cssText = `position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; opacity: ${banner.textureIntensity};`;
          scaledContainer.appendChild(textureOverlay);
        }

        // Append the centered scaledContainer to videoWrapper
        videoWrapper.appendChild(scaledContainer);
      });
      return;
    }

    // For images only: use canvas approach
    // Remove video wrapper if exists
    mockupContainers.forEach(container => {
      const videoWrapper = container.querySelector('.mockup-video-wrapper');
      if (videoWrapper) videoWrapper.remove();
    });

    // Create preview canvas
    const previewCanvas = document.createElement('canvas');
    previewCanvas.width = banner.outputWidth;
    previewCanvas.height = banner.outputHeight;
    const ctx = previewCanvas.getContext('2d');

    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, previewCanvas.width, previewCanvas.height);

    const drawPromises = banner.selectedLayout.panels.map((panelDef, index) => {
      return new Promise((resolve) => {
        const panelData = banner.panels[index];

        const px = panelDef.x * banner.outputWidth;
        const py = panelDef.y * banner.outputHeight;
        const pw = panelDef.w * banner.outputWidth;
        const ph = panelDef.h * banner.outputHeight;

        ctx.fillStyle = '#1a1a1a';
        ctx.fillRect(px, py, pw, ph);

        if (panelData && panelData.image) {
          // Draw image for mockup preview
          const img = new Image();
          img.onload = () => {
            ctx.save();
            ctx.beginPath();
            ctx.rect(px, py, pw, ph);
            ctx.clip();

            const imgWidth = panelData.naturalWidth * panelData.scale;
            const imgHeight = panelData.naturalHeight * panelData.scale;
            ctx.drawImage(img, px + panelData.x, py + panelData.y, imgWidth, imgHeight);

            ctx.restore();
            resolve();
          };
          img.onerror = resolve;
          img.src = panelData.image;
        } else {
          resolve();
        }
      });
    });

    Promise.all(drawPromises).then(() => {
      // Draw grid lines on canvas if enabled (using segments for complex layouts)
      if (banner.gridLineEnabled && banner.panelCount > 1) {
        const linePositions = calculateGridLinePositions(banner.selectedLayout, banner.outputWidth, banner.outputHeight);
        ctx.strokeStyle = banner.gridLineColor;
        ctx.lineWidth = banner.gridLineWidth;
        ctx.lineCap = 'butt';

        linePositions.forEach(line => {
          ctx.beginPath();
          if (line.type === 'vertical') {
            ctx.moveTo(line.position, line.start);
            ctx.lineTo(line.position, line.end);
          } else {
            ctx.moveTo(line.start, line.position);
            ctx.lineTo(line.end, line.position);
          }
          ctx.stroke();
        });
      }

      mockupContainers.forEach(container => {
        const placeholder = container.querySelector('.mockup-placeholder');
        if (placeholder) placeholder.style.display = 'none';

        let canvas = container.querySelector('canvas');
        if (!canvas) {
          canvas = document.createElement('canvas');
          container.appendChild(canvas);
        }
        canvas.width = previewCanvas.width;
        canvas.height = previewCanvas.height;
        canvas.getContext('2d').drawImage(previewCanvas, 0, 0);

        // Add texture overlay to mockup if enabled (CSS overlay for performance)
        // Remove existing texture overlay
        const existingTextureOverlay = container.querySelector('.mockup-texture-overlay');
        if (existingTextureOverlay) existingTextureOverlay.remove();

        if (banner.textureType) {
          const textureOverlay = document.createElement('div');
          textureOverlay.className = `mockup-texture-overlay texture-preview-overlay texture-${banner.textureType}`;
          textureOverlay.style.cssText = `position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; opacity: ${banner.textureIntensity}; border-radius: inherit;`;
          container.appendChild(textureOverlay);
        }
      });
    });
  }

  // ============================================
  // Update Format Buttons (disable PNG/JPEG/WebP when video is used)
  // ============================================
  function updateFormatButtons(bannerIndex, row) {
    const banner = state.banners[bannerIndex];
    const hasVideo = banner.panels.some(p => p && p.isVideo);

    const formatBtns = row.querySelectorAll('.format-btn');
    formatBtns.forEach(btn => {
      const format = btn.dataset.format;
      if (hasVideo) {
        // Video: disable image formats, only MP4 (static frame) allowed
        // For now, allow PNG/JPEG/WebP for static frame capture
        // Add MP4 button if not exists
        btn.disabled = false;
        btn.classList.remove('format-btn--disabled');
      } else {
        // Image: normal behavior
        if (format === 'webp' && !state.supportsWebP) {
          btn.disabled = true;
          btn.classList.add('format-btn--disabled');
        } else {
          btn.disabled = false;
          btn.classList.remove('format-btn--disabled');
        }
      }
    });

    // Update export button text if video
    const exportBtn = row.querySelector('.banner-lab-v4__export-btn');
    if (exportBtn) {
      const hasContent = banner.panels.some(p => p && p.image);
      if (hasContent && hasVideo) {
        exportBtn.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          ${state.locale === 'ja' ? 'フレームをダウンロード' : 'DOWNLOAD FRAME'}
        `;
      } else {
        exportBtn.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          ${state.locale === 'ja' ? 'ダウンロード' : 'DOWNLOAD'}
        `;
      }
    }
  }

  // ============================================
  // SVG-based Texture Overlay Effect
  // Renders texture using SVG filters and composites via overlay blend
  // ============================================
  function applyTextureEffect(ctx, width, height, textureType, intensity = 0.3) {
    if (!textureType) return Promise.resolve();

    return new Promise((resolve) => {
      // Create off-screen canvas for texture
      const textureCanvas = document.createElement('canvas');
      textureCanvas.width = width;
      textureCanvas.height = height;
      const textureCtx = textureCanvas.getContext('2d');

      // Create SVG data URL with the filter
      // CALIBRATED texture filters - matched to CSS preview for WYSIWYG experience
      const filterDefs = {
        // Digital Noise: Subtle cyberpunk-style noise (matched to CSS preview)
        digital: `
          <filter id="f" x="0" y="0" width="100%" height="100%">
            <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="4" seed="${Math.floor(Math.random() * 1000)}" stitchTiles="stitch" result="noise"/>
            <feColorMatrix type="saturate" values="0" in="noise" result="mono"/>
            <feComponentTransfer in="mono" result="contrast">
              <feFuncR type="linear" slope="1.5" intercept="-0.25"/>
              <feFuncG type="linear" slope="1.5" intercept="-0.25"/>
              <feFuncB type="linear" slope="1.5" intercept="-0.25"/>
            </feComponentTransfer>
          </filter>
        `,
        // Film Grain: Balanced analog film look (matched to CSS preview)
        film: `
          <filter id="f" x="0" y="0" width="100%" height="100%">
            <feTurbulence type="turbulence" baseFrequency="0.8" numOctaves="5" seed="${Math.floor(Math.random() * 1000)}" result="noise"/>
            <feDiffuseLighting in="noise" lighting-color="#fff" surfaceScale="3" result="light">
              <feDistantLight azimuth="45" elevation="70"/>
            </feDiffuseLighting>
            <feColorMatrix type="saturate" values="0" in="light" result="mono"/>
            <feComponentTransfer in="mono">
              <feFuncR type="linear" slope="2.5" intercept="-0.75"/>
              <feFuncG type="linear" slope="2.5" intercept="-0.75"/>
              <feFuncB type="linear" slope="2.5" intercept="-0.75"/>
            </feComponentTransfer>
          </filter>
        `,
        // Paper Texture: Subtle paper fiber texture (matched to CSS preview)
        paper: `
          <filter id="f" x="0" y="0" width="100%" height="100%">
            <feTurbulence type="fractalNoise" baseFrequency="0.02" numOctaves="6" seed="${Math.floor(Math.random() * 1000)}" result="noise"/>
            <feDiffuseLighting in="noise" lighting-color="#f8f8f0" surfaceScale="4" result="light">
              <feDistantLight azimuth="135" elevation="35"/>
            </feDiffuseLighting>
            <feColorMatrix type="saturate" values="0" in="light" result="mono"/>
            <feComponentTransfer in="mono">
              <feFuncR type="linear" slope="2" intercept="-0.5"/>
              <feFuncG type="linear" slope="2" intercept="-0.5"/>
              <feFuncB type="linear" slope="2" intercept="-0.5"/>
            </feComponentTransfer>
          </filter>
        `
      };

      const filterDef = filterDefs[textureType];
      if (!filterDef) {
        resolve();
        return;
      }

      // Create SVG with filter and a filled rect
      const svgString = `
        <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
          <defs>${filterDef}</defs>
          <rect width="100%" height="100%" filter="url(#f)" fill="#808080"/>
        </svg>
      `;

      const svgBlob = new Blob([svgString], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(svgBlob);

      const img = new Image();
      img.onload = () => {
        // Draw texture to temp canvas
        textureCtx.drawImage(img, 0, 0, width, height);
        URL.revokeObjectURL(url);

        // Apply texture to main canvas using overlay blend mode
        ctx.save();
        ctx.globalAlpha = intensity;
        ctx.globalCompositeOperation = 'overlay';
        ctx.drawImage(textureCanvas, 0, 0);
        ctx.restore();

        resolve();
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        console.warn('[BannerLab] Failed to load texture SVG');
        resolve();
      };
      img.src = url;
    });
  }

  // Legacy function for backward compatibility
  function applyGrainEffect(ctx, width, height, intensity = 0.15) {
    // Use film grain as default
    return applyTextureEffect(ctx, width, height, 'film', intensity);
  }

  // ============================================
  // Real-time Texture Preview Overlay
  // Shows texture effect on canvas without re-rendering
  // ============================================
  function updateTexturePreview(bannerIndex, row, textureType, intensity = 0.3) {
    const panelsContainer = row.querySelector('.banner-lab-v4__panels-container');
    if (!panelsContainer) return;

    // Remove existing overlay
    const existingOverlay = panelsContainer.querySelector('.texture-preview-overlay');
    if (existingOverlay) {
      existingOverlay.remove();
    }

    // If no texture, we're done
    if (!textureType) return;

    // Create and add new overlay
    const overlay = document.createElement('div');
    overlay.className = `texture-preview-overlay texture-${textureType}`;
    overlay.style.opacity = intensity;
    panelsContainer.appendChild(overlay);
  }

  // ============================================
  // Download Banner (Image or Video)
  // ============================================
  async function downloadBanner(bannerIndex) {
    // Login check - require login for downloads
    if (!isUserLoggedIn()) {
      showLoginRequiredModal();
      return;
    }

    const banner = state.banners[bannerIndex];
    const hasVideo = banner.panels.some(p => p && p.isVideo);

    if (hasVideo) {
      // Download as MP4 video
      await downloadBannerAsVideo(bannerIndex);
    } else {
      // Download as image
      downloadBannerAsImage(bannerIndex);
    }
  }

  // ============================================
  // Video Conversion Helper
  // ============================================
  // Server-side conversion endpoint (for future use)
  const VIDEO_CONVERT_ENDPOINT = 'https://download-broker-343305593182.asia-northeast1.run.app/api/convert-video';

  async function convertToMP4(webmBlob, bannerIndex, banner) {
    // For now, return the blob as-is with .mp4 extension
    // Most modern players can play WebM files even with .mp4 extension
    // TODO: Implement server-side conversion if needed
    return webmBlob;
  }

  // ============================================
  // Download Banner as MP4 Video
  // ============================================
  async function downloadBannerAsVideo(bannerIndex) {
    const banner = state.banners[bannerIndex];
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = banner.outputWidth;
    canvas.height = banner.outputHeight;

    // Pre-load all static images as Image elements for use in renderFrame
    const staticImageElements = {};
    await Promise.all(banner.panels.map((panel, idx) => {
      return new Promise((resolve) => {
        if (panel && panel.image && !panel.isVideo) {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            staticImageElements[idx] = img;
            resolve();
          };
          img.onerror = () => {
            console.warn('[Banner Lab] Failed to load static image for panel', idx);
            resolve();
          };
          img.src = panel.image;
        } else {
          resolve();
        }
      });
    }));
    if (DEBUG) console.log('[Banner Lab] Frame download - Pre-loaded static images:', Object.keys(staticImageElements));

    // Get all video elements and find the longest duration
    const videoElements = [];
    let maxDuration = 0;

    banner.panels.forEach((panelData, index) => {
      if (panelData && panelData.isVideo && panelData.videoElement) {
        const video = panelData.videoElement;
        videoElements.push({ index, video, panelData });
        if (video.duration && video.duration > maxDuration) {
          maxDuration = video.duration;
        }
      }
    });

    // Default to 5 seconds if no valid duration
    if (!maxDuration || !isFinite(maxDuration)) {
      maxDuration = 5;
    }

    // Cap at 30 seconds to prevent huge files
    maxDuration = Math.min(maxDuration, 30);

    // Reset all videos to start
    videoElements.forEach(({ video }) => {
      video.currentTime = 0;
      video.play().catch(() => {});
    });

    // Show progress indicator
    const exportBtn = document.querySelector(`.banner-lab-v4__export-btn[data-banner-index="${bannerIndex}"]`);
    const originalBtnText = exportBtn ? exportBtn.innerHTML : '';
    if (exportBtn) {
      exportBtn.innerHTML = `<svg class="spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg> Recording...`;
      exportBtn.disabled = true;
    }

    // Set up MediaRecorder - prefer MP4 if supported, fallback to WebM
    const stream = canvas.captureStream(30); // 30 FPS

    // Try to use MP4 first (Chrome 94+, Edge 94+ support it)
    let mimeType = 'video/webm;codecs=vp9';
    let fileExtension = 'webm';

    if (MediaRecorder.isTypeSupported('video/mp4')) {
      mimeType = 'video/mp4';
      fileExtension = 'mp4';
    } else if (MediaRecorder.isTypeSupported('video/webm;codecs=h264')) {
      // Some browsers support H.264 in WebM container
      mimeType = 'video/webm;codecs=h264';
      fileExtension = 'mp4'; // Can often be renamed to .mp4
    }

    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: mimeType,
      videoBitsPerSecond: 8000000 // 8 Mbps for good quality
    });

    const chunks = [];
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunks.push(e.data);
      }
    };

    mediaRecorder.onstop = async () => {
      // Update button to show converting
      if (exportBtn) {
        exportBtn.innerHTML = `<svg class="spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg> Converting...`;
      }

      const webmBlob = new Blob(chunks, { type: mimeType });

      // If native MP4, download directly
      if (mimeType === 'video/mp4') {
        downloadBlob(webmBlob, `banner_${bannerIndex + 1}_${banner.outputWidth}x${banner.outputHeight}.mp4`);
        resetButton();
        return;
      }

      // Download as MP4 (WebM with .mp4 extension works in most players)
      // Note: For true MP4 compatibility, use video editing software or online converters
      downloadBlob(webmBlob, `banner_${bannerIndex + 1}_${banner.outputWidth}x${banner.outputHeight}.mp4`);

      resetButton();

      function resetButton() {
        if (exportBtn) {
          exportBtn.innerHTML = originalBtnText;
          exportBtn.disabled = false;
        }
        // Stop all videos
        videoElements.forEach(({ video }) => {
          video.pause();
        });
      }

      function downloadBlob(blob, filename) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = filename;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
      }
    };

    // Start recording
    mediaRecorder.start();

    // Render loop - use fixed interval for smoother video capture
    const startTime = performance.now();
    const durationMs = maxDuration * 1000;
    const targetFPS = 30;
    const frameInterval = 1000 / targetFPS;
    let lastFrameTime = 0;
    let animationId = null;

    function renderFrame(currentTime) {
      const elapsed = performance.now() - startTime;

      if (elapsed >= durationMs) {
        mediaRecorder.stop();
        return;
      }

      // Throttle to target FPS for consistent timing
      if (currentTime - lastFrameTime >= frameInterval) {
        lastFrameTime = currentTime;

        // Clear canvas
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw each panel
        banner.selectedLayout.panels.forEach((panelDef, index) => {
          const panelData = banner.panels[index];

          const px = panelDef.x * banner.outputWidth;
          const py = panelDef.y * banner.outputHeight;
          const pw = panelDef.w * banner.outputWidth;
          const ph = panelDef.h * banner.outputHeight;

          ctx.fillStyle = '#1a1a1a';
          ctx.fillRect(px, py, pw, ph);

          if (panelData && panelData.image) {
            ctx.save();
            ctx.beginPath();
            ctx.rect(px, py, pw, ph);
            ctx.clip();

            const imgWidth = panelData.naturalWidth * panelData.scale;
            const imgHeight = panelData.naturalHeight * panelData.scale;

            if (panelData.isVideo && panelData.videoElement) {
              try {
                // Ensure video is playing
                if (panelData.videoElement.paused) {
                  panelData.videoElement.play().catch(() => {});
                }
                // Only draw if video has enough data
                if (panelData.videoElement.readyState >= 2) {
                  ctx.drawImage(panelData.videoElement, px + panelData.x, py + panelData.y, imgWidth, imgHeight);
                }
              } catch (e) {}
            } else if (staticImageElements[index]) {
              // Draw pre-loaded static image
              ctx.drawImage(staticImageElements[index], px + panelData.x, py + panelData.y, imgWidth, imgHeight);
            }

            ctx.restore();
          }
        });

        // Draw grid lines if enabled (using segments for complex layouts)
        if (banner.gridLineEnabled && banner.panelCount > 1) {
          const linePositions = calculateGridLinePositions(banner.selectedLayout, banner.outputWidth, banner.outputHeight);
          ctx.strokeStyle = banner.gridLineColor;
          ctx.lineWidth = banner.gridLineWidth;
          ctx.lineCap = 'butt';

          linePositions.forEach(line => {
            ctx.beginPath();
            if (line.type === 'vertical') {
              ctx.moveTo(line.position, line.start);
              ctx.lineTo(line.position, line.end);
            } else {
              ctx.moveTo(line.start, line.position);
              ctx.lineTo(line.end, line.position);
            }
            ctx.stroke();
          });
        }
      }

      animationId = requestAnimationFrame(renderFrame);
    }

    // Wait for videos to be ready and start playing
    await Promise.all(videoElements.map(({ video }) => {
      return new Promise((resolve) => {
        video.currentTime = 0;
        const onCanPlay = () => {
          video.removeEventListener('canplay', onCanPlay);
          resolve();
        };
        if (video.readyState >= 3) {
          resolve();
        } else {
          video.addEventListener('canplay', onCanPlay);
        }
      });
    }));

    // Start all videos
    videoElements.forEach(({ video }) => {
      video.play().catch(() => {});
    });

    // Small delay to ensure videos are actually playing
    await new Promise(r => setTimeout(r, 200));

    // Begin rendering
    renderFrame(performance.now());
  }

  // ============================================
  // Download Banner as Image
  // ============================================
  function downloadBannerAsImage(bannerIndex) {
    const banner = state.banners[bannerIndex];
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = banner.outputWidth;
    canvas.height = banner.outputHeight;

    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const drawPromises = banner.selectedLayout.panels.map((panelDef, index) => {
      return new Promise((resolve) => {
        const panelData = banner.panels[index];

        const px = panelDef.x * banner.outputWidth;
        const py = panelDef.y * banner.outputHeight;
        const pw = panelDef.w * banner.outputWidth;
        const ph = panelDef.h * banner.outputHeight;

        ctx.fillStyle = '#1a1a1a';
        ctx.fillRect(px, py, pw, ph);

        if (panelData && panelData.image) {
          // Draw image for download
          const img = new Image();
          img.onload = () => {
            ctx.save();
            ctx.beginPath();
            ctx.rect(px, py, pw, ph);
            ctx.clip();

            const imgWidth = panelData.naturalWidth * panelData.scale;
            const imgHeight = panelData.naturalHeight * panelData.scale;
            ctx.drawImage(img, px + panelData.x, py + panelData.y, imgWidth, imgHeight);

            ctx.restore();
            resolve();
          };
          img.onerror = resolve;
          img.src = panelData.image;
        } else {
          resolve();
        }
      });
    });

    Promise.all(drawPromises).then(async () => {
      // Draw grid lines if enabled (using segments for complex layouts)
      if (banner.gridLineEnabled && banner.panelCount > 1) {
        const linePositions = calculateGridLinePositions(banner.selectedLayout, banner.outputWidth, banner.outputHeight);
        ctx.strokeStyle = banner.gridLineColor;
        ctx.lineWidth = banner.gridLineWidth;
        ctx.lineCap = 'butt';

        linePositions.forEach(line => {
          ctx.beginPath();
          if (line.type === 'vertical') {
            ctx.moveTo(line.position, line.start);
            ctx.lineTo(line.position, line.end);
          } else {
            ctx.moveTo(line.start, line.position);
            ctx.lineTo(line.end, line.position);
          }
          ctx.stroke();
        });
      }

      // Apply SVG-based texture overlay if enabled
      if (banner.textureType) {
        await applyTextureEffect(ctx, banner.outputWidth, banner.outputHeight, banner.textureType, banner.textureIntensity);
      }

      let mimeType, extension, quality;
      let requestedFormat = banner.format;

      switch (requestedFormat) {
        case 'webp':
          if (state.supportsWebP) {
            mimeType = 'image/webp';
            extension = 'webp';
            quality = 0.92;
          } else {
            // Fallback to PNG for Safari and other browsers without WebP canvas support
            mimeType = 'image/png';
            extension = 'png';
            quality = undefined;
          }
          break;
        case 'jpeg':
          mimeType = 'image/jpeg';
          extension = 'jpg';
          quality = 0.92;
          break;
        default:
          mimeType = 'image/png';
          extension = 'png';
          quality = undefined;
      }

      const dataUrl = canvas.toDataURL(mimeType, quality);
      const link = document.createElement('a');
      link.download = `banner_${bannerIndex + 1}_${banner.outputWidth}x${banner.outputHeight}.${extension}`;
      link.href = dataUrl;
      link.click();
    });
  }

  // ============================================
  // Download Mockup as Video (for panels with video content)
  // ============================================
  async function downloadMockupAsVideo(bannerIndex) {
    const banner = state.banners[bannerIndex];
    const bannerRow = document.querySelector(`.banner-lab-v4__banner-row[data-banner-index="${bannerIndex}"]`);
    if (!bannerRow) {
      console.error('Banner row not found');
      return;
    }

    const device = banner.currentDevice || 'apple';
    const platform = banner.currentPlatform || 'instagram';

    // Find the video panel
    const videoPanelIndex = banner.panels.findIndex(p => p && p.isVideo && p.videoElement);
    if (videoPanelIndex === -1) {
      console.error('No video panel found');
      return;
    }
    const videoPanel = banner.panels[videoPanelIndex];
    const sourceVideo = videoPanel.videoElement;

    // Show loading state
    const downloadBtn = bannerRow.querySelector('.banner-lab-v4__mockup-download');
    const originalText = downloadBtn ? downloadBtn.innerHTML : '';
    if (downloadBtn) {
      downloadBtn.innerHTML = `
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="animation: spin 1s linear infinite;">
          <circle cx="12" cy="12" r="10" stroke-dasharray="32" stroke-dashoffset="8"/>
        </svg>
        ${state.locale === 'ja' ? '動画生成中...' : 'Generating video...'}
      `;
      downloadBtn.disabled = true;
    }

    try {
      // Device to frame URL mapping
      const frameUrls = {
        'apple': 'https://verticaly.co/cdn/shop/t/80/assets/mockup-iphone.png',
        'google': 'https://verticaly.co/cdn/shop/t/80/assets/mockup-pixel.png',
        'samsung': 'https://verticaly.co/cdn/shop/t/80/assets/mockup-iphone.png',  // Use iPhone frame
        'huawei': 'https://verticaly.co/cdn/shop/t/80/assets/mockup-iphone.png',   // Use iPhone frame
        'generic': 'https://verticaly.co/cdn/shop/t/80/assets/mockup-iphone.png'   // Use iPhone frame
      };
      const frameUrl = frameUrls[device] || frameUrls['apple'];
      if (DEBUG) console.log('[Banner Lab] Video mockup using device:', device, 'frame:', frameUrl);

      // Load mockup frame
      const frameImg = await new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = frameUrl;
      });

      // Pre-load all static images as Image elements for use in renderFrame
      const staticImageElements = {};
      await Promise.all(banner.panels.map((panel, idx) => {
        return new Promise((resolve) => {
          if (panel && panel.image && !panel.isVideo) {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              staticImageElements[idx] = img;
              resolve();
            };
            img.onerror = () => {
              console.warn('Failed to load static image for panel', idx);
              resolve();
            };
            img.src = panel.image;
          } else {
            resolve();
          }
        });
      }));
      if (DEBUG) console.log('[Banner Lab] Pre-loaded static images:', Object.keys(staticImageElements));

      // Create output canvas (mockup size)
      const canvas = document.createElement('canvas');
      canvas.width = frameImg.width;
      canvas.height = frameImg.height;
      const ctx = canvas.getContext('2d');

      // Device-specific screen coordinates
      const screenConfigs = {
        'apple': { x: 39, y: 39, w: 1093, h: 2304, r: 94 },      // iPhone (1171×2382)
        'google': { x: 42, y: 42, w: 1163, h: 2462, r: 70 },     // Pixel (1247×2546) - corners adjusted
        'samsung': { x: 39, y: 39, w: 1093, h: 2304, r: 94 },    // Use iPhone coords
        'huawei': { x: 39, y: 39, w: 1093, h: 2304, r: 94 },     // Use iPhone coords
        'generic': { x: 39, y: 39, w: 1093, h: 2304, r: 94 }     // Use iPhone coords
      };
      const screenConfig = screenConfigs[device] || screenConfigs['apple'];
      const screenX = screenConfig.x;
      const screenY = screenConfig.y;
      const screenW = screenConfig.w;
      const screenH = screenConfig.h;
      const cornerRadius = screenConfig.r;

      // Helper function for rounded rect
      function roundedRect(ctx, x, y, w, h, r) {
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.lineTo(x + w - r, y);
        ctx.quadraticCurveTo(x + w, y, x + w, y + r);
        ctx.lineTo(x + w, y + h - r);
        ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
        ctx.lineTo(x + r, y + h);
        ctx.quadraticCurveTo(x, y + h, x, y + h - r);
        ctx.lineTo(x, y + r);
        ctx.quadraticCurveTo(x, y, x + r, y);
        ctx.closePath();
      }

      // Create content canvas
      const contentCanvas = document.createElement('canvas');
      contentCanvas.width = banner.outputWidth;
      contentCanvas.height = banner.outputHeight;
      const contentCtx = contentCanvas.getContext('2d');

      // Set up MediaRecorder
      const stream = canvas.captureStream(30); // 30 FPS
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp9',
        videoBitsPerSecond: 5000000 // 5 Mbps
      });

      const chunks = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      // Promise to wait for recording to finish
      const recordingComplete = new Promise((resolve) => {
        mediaRecorder.onstop = () => {
          const blob = new Blob(chunks, { type: 'video/webm' });
          resolve(blob);
        };
      });

      // Start recording
      mediaRecorder.start();

      // Reset video to start
      sourceVideo.currentTime = 0;
      sourceVideo.muted = true;
      await sourceVideo.play();

      // Render function for each frame
      const renderFrame = () => {
        // Clear content canvas
        contentCtx.fillStyle = '#000';
        contentCtx.fillRect(0, 0, contentCanvas.width, contentCanvas.height);

        // Draw ALL panels to content canvas (both video and static images)
        banner.panels.forEach((panel, panelIdx) => {
          if (!panel || !banner.selectedLayout.panels[panelIdx]) return;

          const panelDef = banner.selectedLayout.panels[panelIdx];
          const px = panelDef.x * banner.outputWidth;
          const py = panelDef.y * banner.outputHeight;
          const pw = panelDef.w * banner.outputWidth;
          const ph = panelDef.h * banner.outputHeight;

          contentCtx.save();
          contentCtx.beginPath();
          contentCtx.rect(px, py, pw, ph);
          contentCtx.clip();

          if (panel.isVideo && panel.videoElement) {
            // Draw video frame
            const imgWidth = panel.naturalWidth * panel.scale;
            const imgHeight = panel.naturalHeight * panel.scale;
            contentCtx.drawImage(panel.videoElement, px + panel.x, py + panel.y, imgWidth, imgHeight);
          } else if (staticImageElements[panelIdx]) {
            // Draw static image (pre-loaded)
            const imgWidth = panel.naturalWidth * panel.scale;
            const imgHeight = panel.naturalHeight * panel.scale;
            contentCtx.drawImage(staticImageElements[panelIdx], px + panel.x, py + panel.y, imgWidth, imgHeight);
          }

          contentCtx.restore();
        });

        // Draw to main canvas with mockup
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Clip to screen area
        ctx.save();
        roundedRect(ctx, screenX, screenY, screenW, screenH, cornerRadius);
        ctx.clip();

        // Draw black background
        ctx.fillStyle = '#000';
        ctx.fillRect(screenX, screenY, screenW, screenH);

        // Calculate how to draw content in screen area
        const isReelsLayout = banner.aspectRatio === '9:16';
        const contentAspect = contentCanvas.width / contentCanvas.height;

        if (isReelsLayout) {
          // Reels: cover fit
          const screenAspect = screenW / screenH;
          let drawW, drawH, drawX, drawY;
          if (contentAspect > screenAspect) {
            drawH = screenH;
            drawW = screenH * contentAspect;
            drawX = screenX - (drawW - screenW) / 2;
            drawY = screenY;
          } else {
            drawW = screenW;
            drawH = screenW / contentAspect;
            drawX = screenX;
            drawY = screenY - (drawH - screenH) / 2;
          }
          ctx.drawImage(contentCanvas, drawX, drawY, drawW, drawH);
        } else {
          // Feed: header + image + footer
          const imageTopPercent = 0.1233;
          const imageStartY = screenY + screenH * imageTopPercent;
          const drawW = screenW;
          const drawH = screenW / contentAspect;
          ctx.drawImage(contentCanvas, screenX, imageStartY, drawW, drawH);
        }

        ctx.restore();

        // ============================================
        // Draw UI Overlay (same as static mockup)
        // ============================================
        const isReelsFormat = banner.aspectRatio === '9:16';

        // SVG paths for icons
        const heartPathD = 'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z';
        const commentPathD = 'M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z';

        // Helper function to draw SVG path
        function drawSvgPath(ctx, pathD, x, y, scale, stroke = true) {
          const path = new Path2D(pathD);
          ctx.save();
          ctx.translate(x, y);
          ctx.scale(scale, scale);
          if (stroke) { ctx.stroke(path); } else { ctx.fill(path); }
          ctx.restore();
        }

        ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
        ctx.shadowBlur = 8;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 2;

        if (platform === 'instagram' && isReelsFormat) {
          // Instagram Reels UI
          const scaleX = canvas.width / 190;
          const scaleY = canvas.height / 410;

          // Sidebar icons
          const heartX = 152 * scaleX, heartY = 193 * scaleY;
          ctx.save();
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 1.8;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          drawSvgPath(ctx, heartPathD, heartX, heartY, 5.5, true);
          ctx.restore();

          // Like count
          ctx.fillStyle = '#ffffff';
          ctx.font = '600 52px -apple-system, BlinkMacSystemFont, sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText('24.5K', 162 * scaleX, 229 * scaleY);

          // Comment icon
          ctx.save();
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 1.8;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          drawSvgPath(ctx, commentPathD, 153 * scaleX, 242 * scaleY, 5.0, true);
          ctx.restore();
          ctx.fillText('892', 162 * scaleX, 276 * scaleY);

          // Audio disc
          ctx.save();
          ctx.shadowBlur = 0;
          const audioDiscSize = 24 * scaleX;
          const audioDiscX = 150 * scaleX, audioDiscY = 288 * scaleY;
          const audioGradient = ctx.createLinearGradient(audioDiscX, audioDiscY, audioDiscX + audioDiscSize, audioDiscY + audioDiscSize);
          audioGradient.addColorStop(0, '#833ab4');
          audioGradient.addColorStop(0.5, '#fd1d1d');
          audioGradient.addColorStop(1, '#fcb045');
          ctx.beginPath();
          ctx.arc(audioDiscX + audioDiscSize/2, audioDiscY + audioDiscSize/2, audioDiscSize/2, 0, Math.PI * 2);
          ctx.fillStyle = audioGradient;
          ctx.fill();
          ctx.beginPath();
          ctx.arc(audioDiscX + audioDiscSize/2, audioDiscY + audioDiscSize/2, audioDiscSize/4, 0, Math.PI * 2);
          ctx.fillStyle = '#222';
          ctx.fill();
          ctx.restore();

          // Avatar + username at bottom
          const avatarSize = 24 * scaleX;
          const avatarX = 17 * scaleX, avatarY = 346 * scaleY;
          ctx.save();
          ctx.shadowBlur = 0;
          ctx.beginPath();
          ctx.arc(avatarX + avatarSize/2, avatarY + avatarSize/2, avatarSize/2 + 6, 0, Math.PI * 2);
          ctx.fillStyle = '#ffffff';
          ctx.fill();
          const avatarGradient = ctx.createLinearGradient(avatarX, avatarY, avatarX + avatarSize, avatarY + avatarSize);
          avatarGradient.addColorStop(0, '#833ab4');
          avatarGradient.addColorStop(0.5, '#fd1d1d');
          avatarGradient.addColorStop(1, '#fcb045');
          ctx.beginPath();
          ctx.arc(avatarX + avatarSize/2, avatarY + avatarSize/2, avatarSize/2, 0, Math.PI * 2);
          ctx.fillStyle = avatarGradient;
          ctx.fill();
          ctx.restore();

          ctx.shadowBlur = 8;
          ctx.fillStyle = '#ffffff';
          ctx.font = '600 56px -apple-system, BlinkMacSystemFont, sans-serif';
          ctx.textAlign = 'left';
          ctx.fillText('your_brand', 47 * scaleX, (349 + 14) * scaleY);

        } else if (platform === 'instagram' && !isReelsFormat) {
          // Instagram Feed UI
          const scaleX = screenW / 175.17;
          const scaleY = screenH / 373.09;
          const padding = 8 * scaleX;
          const imageTopPercent = 0.1233;
          const imageStartY = screenY + screenH * imageTopPercent;
          const imageHeight = screenW / contentAspect;
          const imageBottomY = imageStartY + imageHeight;

          // Header avatar
          const headerHeight = screenH * 0.1233;
          const headerAvatarSize = 18 * scaleX;
          const headerAvatarX = screenX + padding + 2 * scaleX;
          const headerAvatarY = screenY + (headerHeight - headerAvatarSize) / 2 + 30; // +30 to avoid notch

          ctx.save();
          ctx.shadowBlur = 0;
          const headerGradient = ctx.createLinearGradient(headerAvatarX, headerAvatarY, headerAvatarX + headerAvatarSize, headerAvatarY + headerAvatarSize);
          headerGradient.addColorStop(0, '#833ab4');
          headerGradient.addColorStop(0.5, '#fd1d1d');
          headerGradient.addColorStop(1, '#fcb045');
          ctx.beginPath();
          ctx.arc(headerAvatarX + headerAvatarSize/2, headerAvatarY + headerAvatarSize/2, headerAvatarSize/2, 0, Math.PI * 2);
          ctx.fillStyle = headerGradient;
          ctx.fill();
          ctx.restore();

          // Header username
          ctx.shadowBlur = 0;
          ctx.fillStyle = '#ffffff';
          const usernameFontSize = 9 * scaleX;
          ctx.font = `600 ${Math.round(usernameFontSize)}px -apple-system, BlinkMacSystemFont, sans-serif`;
          ctx.textAlign = 'left';
          const headerUsernameX = headerAvatarX + headerAvatarSize + 6 * scaleX;
          const headerUsernameY = headerAvatarY + headerAvatarSize / 2 + usernameFontSize / 3;
          ctx.fillText('your_brand', headerUsernameX, headerUsernameY);

          // Bottom icons
          const iconSize = 16 * scaleX;
          const iconScale = iconSize / 24;
          const iconY = imageBottomY + 4 * scaleY;
          const iconStartX = screenX + padding;
          const iconGap = 8 * scaleX;

          ctx.save();
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2 * (scaleX / 6);
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          drawSvgPath(ctx, heartPathD, iconStartX, iconY, iconScale, true);
          drawSvgPath(ctx, commentPathD, iconStartX + iconSize + iconGap, iconY, iconScale, true);
          const sharePath = 'M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z';
          drawSvgPath(ctx, sharePath, iconStartX + (iconSize + iconGap) * 2, iconY, iconScale, true);
          const bookmarkPath = 'M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z';
          drawSvgPath(ctx, bookmarkPath, screenX + screenW - padding - iconSize, iconY, iconScale, true);
          ctx.restore();

          // Likes count
          const likesFontSize = 9 * scaleX;
          const likesY = iconY + iconSize + 4 * scaleY;
          ctx.fillStyle = '#ffffff';
          ctx.font = `600 ${Math.round(likesFontSize)}px -apple-system, BlinkMacSystemFont, sans-serif`;
          ctx.fillText('1,234 likes', iconStartX, likesY + likesFontSize);

          // Caption
          const captionFontSize = 8 * scaleX;
          const captionY = likesY + likesFontSize + 4 * scaleY;
          ctx.font = `600 ${Math.round(captionFontSize)}px -apple-system, BlinkMacSystemFont, sans-serif`;
          ctx.fillText('your_brand', iconStartX, captionY + captionFontSize);
          ctx.fillStyle = '#cccccc';
          ctx.font = `400 ${Math.round(captionFontSize)}px -apple-system, BlinkMacSystemFont, sans-serif`;
          ctx.fillText('キャプションを入力...', iconStartX + ctx.measureText('your_brand ').width, captionY + captionFontSize);

        } else if (platform === 'tiktok') {
          // TikTok UI
          const sidebarX = screenX + screenW - 70;
          let iconY = screenY + screenH - 480;

          ctx.save();
          ctx.fillStyle = '#ffffff';
          ctx.beginPath();
          const hx = sidebarX, hy = iconY, hs = 22;
          ctx.moveTo(hx, hy + hs * 0.3);
          ctx.bezierCurveTo(hx, hy - hs * 0.3, hx - hs, hy - hs * 0.5, hx - hs, hy + hs * 0.1);
          ctx.bezierCurveTo(hx - hs, hy + hs * 0.7, hx, hy + hs, hx, hy + hs * 1.3);
          ctx.bezierCurveTo(hx, hy + hs, hx + hs, hy + hs * 0.7, hx + hs, hy + hs * 0.1);
          ctx.bezierCurveTo(hx + hs, hy - hs * 0.5, hx, hy - hs * 0.3, hx, hy + hs * 0.3);
          ctx.fill();
          ctx.restore();

          ctx.fillStyle = '#ffffff';
          ctx.font = 'bold 26px -apple-system, BlinkMacSystemFont, sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText('24.5K', sidebarX, iconY + 55);

          iconY += 115;
          ctx.save();
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 4;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.beginPath();
          const cx = sidebarX, cy = iconY, bw = 20, bh = 16, br = 5;
          ctx.moveTo(cx - bw + br, cy - bh);
          ctx.lineTo(cx + bw - br, cy - bh);
          ctx.arcTo(cx + bw, cy - bh, cx + bw, cy - bh + br, br);
          ctx.lineTo(cx + bw, cy + bh - br);
          ctx.arcTo(cx + bw, cy + bh, cx + bw - br, cy + bh, br);
          ctx.lineTo(cx - 4, cy + bh);
          ctx.lineTo(cx - 10, cy + bh + 12);
          ctx.lineTo(cx - 5, cy + bh);
          ctx.lineTo(cx - bw + br, cy + bh);
          ctx.arcTo(cx - bw, cy + bh, cx - bw, cy + bh - br, br);
          ctx.lineTo(cx - bw, cy - bh + br);
          ctx.arcTo(cx - bw, cy - bh, cx - bw + br, cy - bh, br);
          ctx.stroke();
          ctx.restore();

          ctx.fillStyle = '#ffffff';
          ctx.fillText('892', sidebarX, iconY + 52);

          // Username at bottom
          ctx.textAlign = 'left';
          ctx.font = 'bold 32px -apple-system, BlinkMacSystemFont, sans-serif';
          ctx.fillText('@your_brand', screenX + 50, screenY + screenH - 150);
        }

        // Draw mockup frame on top
        ctx.drawImage(frameImg, 0, 0);
      };

      // Animation loop with FPS throttling for smoother video capture
      const targetFPS = 30;
      const frameInterval = 1000 / targetFPS;
      const startTime = performance.now();
      const duration = sourceVideo.duration * 1000; // ms
      let animationId;
      let lastFrameTime = 0;

      const animate = (currentTime) => {
        const elapsed = performance.now() - startTime;

        if (elapsed < duration && !sourceVideo.paused && !sourceVideo.ended) {
          // FPS throttling: only render at target FPS
          const timeSinceLastFrame = currentTime - lastFrameTime;

          if (timeSinceLastFrame >= frameInterval) {
            // Only render if video is ready
            if (sourceVideo.readyState >= 2) {
              renderFrame();
            }
            lastFrameTime = currentTime - (timeSinceLastFrame % frameInterval);
          }

          animationId = requestAnimationFrame(animate);
        } else {
          // Stop recording
          sourceVideo.pause();
          mediaRecorder.stop();
        }
      };

      // Wait for video to be ready before starting
      await new Promise(resolve => {
        if (sourceVideo.readyState >= 3) {
          resolve();
        } else {
          sourceVideo.addEventListener('canplay', resolve, { once: true });
        }
      });

      // Small delay to ensure everything is ready
      await new Promise(r => setTimeout(r, 100));

      // Start animation
      animationId = requestAnimationFrame(animate);

      // Wait for recording to complete
      const videoBlob = await recordingComplete;

      // Download
      const url = URL.createObjectURL(videoBlob);
      const link = document.createElement('a');
      link.download = `mockup_${bannerIndex + 1}_${device}_${platform}.webm`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);

      // Restore button
      if (downloadBtn) {
        downloadBtn.innerHTML = originalText;
        downloadBtn.disabled = false;
      }

    } catch (error) {
      console.error('Video mockup download failed:', error);
      alert(state.locale === 'ja' ? '動画モックアップのダウンロードに失敗しました' : 'Failed to download video mockup');

      if (downloadBtn) {
        downloadBtn.innerHTML = originalText || `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7,10 12,15 17,10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> Mockup`;
        downloadBtn.disabled = false;
      }
    }
  }

  // ============================================
  // Download Mockup (HTML2Canvas - captures full UI including Instagram/TikTok elements)
  // ============================================
  async function downloadMockup(bannerIndex) {
    // Login check - require login for downloads
    if (!isUserLoggedIn()) {
      showLoginRequiredModal();
      return;
    }

    const banner = state.banners[bannerIndex];
    const hasImages = banner.panels.some(p => p && (p.image || p.isVideo));

    if (!hasImages) {
      alert(state.locale === 'ja' ? '画像を追加してください' : 'Please add images first');
      return;
    }

    // Check if any panel has a video - if so, export as video file
    const hasVideo = banner.panels.some(p => p && p.isVideo && p.videoElement);
    if (hasVideo) {
      await downloadMockupAsVideo(bannerIndex);
      return;
    }

    // Find the phone frame element for this banner
    const bannerRow = document.querySelector(`.banner-lab-v4__banner-row[data-banner-index="${bannerIndex}"]`);
    if (!bannerRow) {
      console.error('Banner row not found');
      return;
    }

    const device = banner.currentDevice || 'apple';
    const platform = banner.currentPlatform || 'instagram';

    try {
      // Show loading state
      const downloadBtn = bannerRow.querySelector('.banner-lab-v4__mockup-download');
      const originalText = downloadBtn ? downloadBtn.innerHTML : '';
      if (downloadBtn) {
        downloadBtn.innerHTML = `
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="animation: spin 1s linear infinite;">
            <circle cx="12" cy="12" r="10" stroke-dasharray="32" stroke-dashoffset="8"/>
          </svg>
          ${state.locale === 'ja' ? '生成中...' : 'Generating...'}
        `;
        downloadBtn.disabled = true;
      }

      // ============================================
      // MOCKUP FRAME IMAGE COMPOSITING APPROACH
      // Uses user-provided mockup frame image overlay
      // ============================================

      // Device to frame URL mapping
      const frameUrls = {
        'apple': 'https://verticaly.co/cdn/shop/t/80/assets/mockup-iphone.png',
        'google': 'https://verticaly.co/cdn/shop/t/80/assets/mockup-pixel.png',
        'samsung': 'https://verticaly.co/cdn/shop/t/80/assets/mockup-iphone.png',  // Use iPhone frame
        'huawei': 'https://verticaly.co/cdn/shop/t/80/assets/mockup-iphone.png',   // Use iPhone frame
        'generic': 'https://verticaly.co/cdn/shop/t/80/assets/mockup-iphone.png'   // Use iPhone frame
      };
      const frameUrl = frameUrls[device] || frameUrls['apple'];
      if (DEBUG) console.log('[Banner Lab] Static mockup using device:', device, 'frame:', frameUrl);

      // Load the mockup frame image (user-provided PNG with transparent screen area)
      const frameImg = await new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = (e) => {
          console.error('Failed to load mockup frame:', e);
          reject(e);
        };
        // Load from Shopify theme assets (based on selected device)
        img.src = frameUrl;
      });

      // Create canvas matching the frame image size
      const canvas = document.createElement('canvas');
      canvas.width = frameImg.width;
      canvas.height = frameImg.height;
      const ctx = canvas.getContext('2d');

      // Device-specific screen coordinates
      const screenConfigs = {
        'apple': { x: 39, y: 39, w: 1093, h: 2304, r: 94 },      // iPhone (1171×2382)
        'google': { x: 42, y: 42, w: 1163, h: 2462, r: 70 },     // Pixel (1247×2546) - corners adjusted
        'samsung': { x: 39, y: 39, w: 1093, h: 2304, r: 94 },    // Use iPhone coords
        'huawei': { x: 39, y: 39, w: 1093, h: 2304, r: 94 },     // Use iPhone coords
        'generic': { x: 39, y: 39, w: 1093, h: 2304, r: 94 }     // Use iPhone coords
      };
      const screenConfig = screenConfigs[device] || screenConfigs['apple'];
      const screenX = screenConfig.x;
      const screenY = screenConfig.y;
      const screenW = screenConfig.w;
      const screenH = screenConfig.h;
      const cornerRadius = screenConfig.r;

      // Helper function to draw rounded rectangle
      function roundedRect(ctx, x, y, w, h, r) {
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.lineTo(x + w - r, y);
        ctx.quadraticCurveTo(x + w, y, x + w, y + r);
        ctx.lineTo(x + w, y + h - r);
        ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
        ctx.lineTo(x + r, y + h);
        ctx.quadraticCurveTo(x, y + h, x, y + h - r);
        ctx.lineTo(x, y + r);
        ctx.quadraticCurveTo(x, y, x + r, y);
        ctx.closePath();
      }

      // ============================================
      // STEP 1: Create content canvas with ALL panels rendered
      // This matches the preview exactly - all panels, grid lines, and textures
      // ============================================
      const contentCanvas = document.createElement('canvas');
      contentCanvas.width = banner.outputWidth;
      contentCanvas.height = banner.outputHeight;
      const contentCtx = contentCanvas.getContext('2d');

      // Fill with black background
      contentCtx.fillStyle = '#000';
      contentCtx.fillRect(0, 0, contentCanvas.width, contentCanvas.height);

      // Draw ALL panels with their grid positions
      const panelDrawPromises = banner.selectedLayout.panels.map((panelDef, index) => {
        return new Promise((resolve) => {
          const panelData = banner.panels[index];

          // Calculate panel position and size in output coordinates
          const px = panelDef.x * banner.outputWidth;
          const py = panelDef.y * banner.outputHeight;
          const pw = panelDef.w * banner.outputWidth;
          const ph = panelDef.h * banner.outputHeight;

          // Fill panel background
          contentCtx.fillStyle = '#1a1a1a';
          contentCtx.fillRect(px, py, pw, ph);

          if (panelData && (panelData.image || panelData.isVideo)) {
            // Handle video frame capture
            if (panelData.isVideo && panelData.videoElement) {
              contentCtx.save();
              contentCtx.beginPath();
              contentCtx.rect(px, py, pw, ph);
              contentCtx.clip();

              const video = panelData.videoElement;
              const imgWidth = panelData.naturalWidth * panelData.scale;
              const imgHeight = panelData.naturalHeight * panelData.scale;
              contentCtx.drawImage(video, px + panelData.x, py + panelData.y, imgWidth, imgHeight);

              contentCtx.restore();
              resolve();
            } else if (panelData.image) {
              // Draw image
              const img = new Image();
              img.crossOrigin = 'anonymous';
              img.onload = () => {
                contentCtx.save();
                contentCtx.beginPath();
                contentCtx.rect(px, py, pw, ph);
                contentCtx.clip();

                const imgWidth = panelData.naturalWidth * panelData.scale;
                const imgHeight = panelData.naturalHeight * panelData.scale;
                contentCtx.drawImage(img, px + panelData.x, py + panelData.y, imgWidth, imgHeight);

                contentCtx.restore();
                resolve();
              };
              img.onerror = resolve;
              img.src = panelData.image;
            } else {
              resolve();
            }
          } else {
            resolve();
          }
        });
      });

      // Wait for all panels to be drawn
      await Promise.all(panelDrawPromises);

      // Draw grid lines if enabled (matching preview exactly)
      if (banner.gridLineEnabled && banner.panelCount > 1) {
        const linePositions = calculateGridLinePositions(banner.selectedLayout, banner.outputWidth, banner.outputHeight);
        contentCtx.strokeStyle = banner.gridLineColor;
        contentCtx.lineWidth = banner.gridLineWidth;
        contentCtx.lineCap = 'butt';

        linePositions.forEach(line => {
          contentCtx.beginPath();
          if (line.type === 'vertical') {
            contentCtx.moveTo(line.position, line.start);
            contentCtx.lineTo(line.position, line.end);
          } else {
            contentCtx.moveTo(line.start, line.position);
            contentCtx.lineTo(line.end, line.position);
          }
          contentCtx.stroke();
        });
      }

      // Apply texture overlay if enabled
      if (banner.textureType) {
        await applyTextureEffect(contentCtx, banner.outputWidth, banner.outputHeight, banner.textureType, banner.textureIntensity);
      }

      // Determine if this is Reels (9:16) or Feed (other aspect ratios) for layout
      const isReelsLayout = banner.aspectRatio === '9:16';

      // ============================================
      // STEP 2: Draw content canvas to mockup screen area (with clipping for corners)
      // ============================================
      ctx.save();
      roundedRect(ctx, screenX, screenY, screenW, screenH, cornerRadius);
      ctx.clip();

      // Calculate how to fit the content canvas into the screen area
      const contentAspect = contentCanvas.width / contentCanvas.height;
      let drawW, drawH, drawX, drawY;

      if (isReelsLayout) {
        // REELS: Cover fit - content fills entire screen
        const screenAspect = screenW / screenH;
        if (contentAspect > screenAspect) {
          drawH = screenH;
          drawW = screenH * contentAspect;
          drawX = screenX - (drawW - screenW) / 2;
          drawY = screenY;
        } else {
          drawW = screenW;
          drawH = screenW / contentAspect;
          drawX = screenX;
          drawY = screenY - (drawH - screenH) / 2;
        }
        ctx.drawImage(contentCanvas, drawX, drawY, drawW, drawH);
      } else {
        // FEED: Black background with header, image, and footer
        // Layout based on actual DOM measurements (preview: 190x410)
        ctx.fillStyle = '#000000';
        ctx.fillRect(screenX, screenY, screenW, screenH);

        // ============================================
        // PIXEL-PERFECT LAYOUT from DOM measurements (4:5 Feed)
        // Header: 0% - 12.33% of screen
        // Image: 12.33% - 71.02% of screen (58.69% height for 4:5)
        // Actions: 71.02% - 78.52%
        // Likes: 78.52% - 82.14%
        // Caption: 82.14% - 86.53%
        // ============================================
        const headerHeightPercent = 0.1233;  // From DOM: 12.33%
        const imageTopPercent = 0.1233;      // From DOM: 12.33% (right after header)

        // Image area: full width, starts at 12.33% from screen top
        const imageStartY = screenY + screenH * imageTopPercent;

        // Image fills full width, height determined by content aspect ratio
        drawW = screenW;
        drawH = screenW / contentAspect;
        drawX = screenX;
        drawY = imageStartY;

        // Draw content image
        ctx.drawImage(contentCanvas, drawX, drawY, drawW, drawH);

        // Store image bottom position for UI element positioning
        const imageBottomY = drawY + drawH;
      }

      // Draw UI overlay on top of content (still clipped)
      // ============================================
      // EXACT MATCH TO PREVIEW - Positions calculated from DOM measurements
      // Preview: 190x410 → Output: 1171x2382
      // Scale: X=6.163, Y=5.81
      // ============================================

      // Helper function to draw SVG path with scaling
      function drawSvgPath(ctx, pathD, x, y, scale, stroke = true) {
        const path = new Path2D(pathD);
        ctx.save();
        ctx.translate(x, y);
        ctx.scale(scale, scale);
        if (stroke) {
          ctx.stroke(path);
        } else {
          ctx.fill(path);
        }
        ctx.restore();
      }

      // SVG paths from the actual preview DOM
      const heartPathD = 'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z';
      const commentPathD = 'M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z';

      // Text shadow for readability
      ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
      ctx.shadowBlur = 8;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 2;

      // Determine if this is Reels (9:16) or Feed (other aspect ratios)
      const isReelsFormat = banner.aspectRatio === '9:16';

      if (platform === 'instagram' && isReelsFormat) {
        // ============================================
        // INSTAGRAM REELS UI - PIXEL PERFECT from DOM measurements
        // Preview: 190x410 px → Output: 1171x2382 px
        // All positions calculated from actual DOM getBoundingClientRect()
        // ============================================

        // Scale factors calculated from actual dimensions
        const scaleX = canvas.width / 190;   // 6.163
        const scaleY = canvas.height / 410;  // 5.81

        // --- SIDEBAR ICONS (from DOM: ig-reels__sidebar) ---
        // Heart icon: preview (152, 193), 20x20px
        const heartX = 152 * scaleX;  // 937
        const heartY = 193 * scaleY;  // 1121
        const heartScale = 5.5;       // 20px * 5.5 = 110px output

        // Draw heart icon
        ctx.save();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.8;  // Thinner line to match preview
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        drawSvgPath(ctx, heartPathD, heartX, heartY, heartScale, true);
        ctx.restore();

        // Like count "24.5K": preview (149, 215) -> centered below heart
        // Font size: 14px in preview → 14 * scaleY = 81px, but visually 52px looks better
        ctx.fillStyle = '#ffffff';
        ctx.font = '600 52px -apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif';
        ctx.textAlign = 'center';
        const likeCountX = 162 * scaleX;  // Center of sidebar area
        const likeCountY = 229 * scaleY;  // Below heart icon
        ctx.fillText('24.5K', likeCountX, likeCountY);

        // Comment icon: preview (153, 242), 18x18px
        const commentX = 153 * scaleX;  // 943
        const commentY = 242 * scaleY;  // 1406
        const commentScale = 5.0;       // 18px * 5 = 90px output

        // Draw comment icon
        ctx.save();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.8;  // Thinner line to match preview
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        drawSvgPath(ctx, commentPathD, commentX, commentY, commentScale, true);
        ctx.restore();

        // Comment count "892": preview (153, 262)
        const commentCountY = 276 * scaleY;  // Below comment icon
        ctx.fillText('892', likeCountX, commentCountY);

        // --- AUDIO DISC (from DOM: ig-reels__audio-disc) ---
        // Preview: (150, 288), 24x24px
        const audioDiscSize = 24 * scaleX;  // ~148px
        const audioDiscX = 150 * scaleX;    // 924
        const audioDiscY = 288 * scaleY;    // 1673

        // Draw audio disc with Instagram gradient
        ctx.save();
        ctx.shadowBlur = 0;
        const audioGradient = ctx.createLinearGradient(
          audioDiscX, audioDiscY,
          audioDiscX + audioDiscSize, audioDiscY + audioDiscSize
        );
        audioGradient.addColorStop(0, '#833ab4');
        audioGradient.addColorStop(0.5, '#fd1d1d');
        audioGradient.addColorStop(1, '#fcb045');
        ctx.beginPath();
        ctx.arc(audioDiscX + audioDiscSize/2, audioDiscY + audioDiscSize/2, audioDiscSize/2, 0, Math.PI * 2);
        ctx.fillStyle = audioGradient;
        ctx.fill();
        // Inner circle for disc effect
        ctx.beginPath();
        ctx.arc(audioDiscX + audioDiscSize/2, audioDiscY + audioDiscSize/2, audioDiscSize/4, 0, Math.PI * 2);
        ctx.fillStyle = '#222';
        ctx.fill();
        ctx.restore();

        // --- BOTTOM BAR (from DOM: ig-reels__bottom, ig-reels__avatar, ig-reels__user) ---
        // Avatar: preview (17, 346), 24x24px
        const avatarSize = 24 * scaleX;   // ~148px
        const avatarX = 17 * scaleX;      // 105
        const avatarY = 346 * scaleY;     // 2010

        // Draw avatar with Instagram gradient + WHITE BORDER
        ctx.save();
        ctx.shadowBlur = 0;
        // White border first (larger circle, 2px border in preview = ~12px in output)
        ctx.beginPath();
        ctx.arc(avatarX + avatarSize/2, avatarY + avatarSize/2, avatarSize/2 + 6, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        // Gradient fill
        const avatarGradient = ctx.createLinearGradient(
          avatarX, avatarY,
          avatarX + avatarSize, avatarY + avatarSize
        );
        avatarGradient.addColorStop(0, '#833ab4');
        avatarGradient.addColorStop(0.5, '#fd1d1d');
        avatarGradient.addColorStop(1, '#fcb045');
        ctx.beginPath();
        ctx.arc(avatarX + avatarSize/2, avatarY + avatarSize/2, avatarSize/2, 0, Math.PI * 2);
        ctx.fillStyle = avatarGradient;
        ctx.fill();
        ctx.restore();

        // Username "your_brand": preview (47, 349), spans to ~108px
        // Distance from avatar: 47 - (17 + 24) = 6px gap → 6 * scaleX = ~37px
        ctx.shadowBlur = 8;
        ctx.fillStyle = '#ffffff';
        ctx.font = '600 56px -apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif';
        ctx.textAlign = 'left';
        const usernameX = 47 * scaleX;    // 290
        const usernameY = (349 + 14) * scaleY;  // 2108 (baseline aligned)
        ctx.fillText('your_brand', usernameX, usernameY);

      } else if (platform === 'instagram' && !isReelsFormat) {
        // ============================================
        // INSTAGRAM FEED UI - PIXEL PERFECT from DOM measurements
        // Preview: 190x410 → Output: 1171x2382 (scale ~6.16)
        // All positions calculated from actual DOM getBoundingClientRect()
        // ============================================

        // Scale factor from preview to output
        const scaleX = screenW / 175.17;  // Image width in preview (phone screen width)
        const scaleY = screenH / 373.09;  // Phone screen height in preview

        // Calculate image bottom position (same calculation as in image drawing)
        // From DOM: Image top at 12.33%, height varies by aspect ratio
        const imageTopPercent = 0.1233;
        const imageStartY = screenY + screenH * imageTopPercent;
        const imageHeight = screenW / contentAspect;
        const imageBottomY = imageStartY + imageHeight;

        // Padding from preview (8px at 175px width)
        const padding = 8 * scaleX;

        // --- TOP HEADER (avatar + username) ---
        // From DOM: Header is 0-12.33% of screen height
        // Avatar is vertically centered in the 12.33% header space
        const headerHeight = screenH * 0.1233;
        const headerAvatarSize = 18 * scaleX;
        const headerAvatarX = screenX + padding + 2 * scaleX;
        const headerAvatarY = screenY + (headerHeight - headerAvatarSize) / 2 + 30;  // Center avatar in header + offset to avoid notch

        // Draw header avatar with Instagram gradient
        ctx.save();
        ctx.shadowBlur = 0;
        const headerGradient = ctx.createLinearGradient(
          headerAvatarX, headerAvatarY,
          headerAvatarX + headerAvatarSize, headerAvatarY + headerAvatarSize
        );
        headerGradient.addColorStop(0, '#833ab4');
        headerGradient.addColorStop(0.5, '#fd1d1d');
        headerGradient.addColorStop(1, '#fcb045');
        ctx.beginPath();
        ctx.arc(headerAvatarX + headerAvatarSize/2, headerAvatarY + headerAvatarSize/2, headerAvatarSize/2, 0, Math.PI * 2);
        ctx.fillStyle = headerGradient;
        ctx.fill();
        ctx.restore();

        // Username next to avatar
        // From DOM: font-size 9px, font-weight 600, vertically centered with avatar
        ctx.shadowBlur = 0;
        ctx.fillStyle = '#ffffff';
        const usernameFontSize = 9 * scaleX;  // 9px in preview
        ctx.font = `600 ${Math.round(usernameFontSize)}px -apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif`;
        ctx.textAlign = 'left';
        const headerUsernameX = headerAvatarX + headerAvatarSize + 6 * scaleX;  // 6px gap
        const headerUsernameY = headerAvatarY + headerAvatarSize / 2 + usernameFontSize / 3;  // Vertically centered
        ctx.fillText('your_brand', headerUsernameX, headerUsernameY);

        // --- BOTTOM ICONS (heart, comment, share, bookmark) ---
        // From DOM: icons are 16x16, padding 4px 8px for actions area
        const iconSize = 16 * scaleX;  // 16px in preview
        const iconScale = iconSize / 24;  // SVG paths are 24x24
        const iconY = imageBottomY + 4 * scaleY;  // 4px padding after image
        const iconStartX = screenX + padding;
        const iconGap = 8 * scaleX;  // 8px gap between icons

        // Heart icon (outline)
        ctx.save();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2 * (scaleX / 6);  // Scale line width
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        drawSvgPath(ctx, heartPathD, iconStartX, iconY, iconScale, true);
        ctx.restore();

        // Comment icon
        ctx.save();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2 * (scaleX / 6);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        drawSvgPath(ctx, commentPathD, iconStartX + iconSize + iconGap, iconY, iconScale, true);
        ctx.restore();

        // Share icon (paper plane)
        const sharePath = 'M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z';
        ctx.save();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2 * (scaleX / 6);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        drawSvgPath(ctx, sharePath, iconStartX + (iconSize + iconGap) * 2, iconY, iconScale, true);
        ctx.restore();

        // Bookmark icon (on the right side)
        const bookmarkPath = 'M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z';
        ctx.save();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2 * (scaleX / 6);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        const bookmarkX = screenX + screenW - padding - iconSize;
        drawSvgPath(ctx, bookmarkPath, bookmarkX, iconY, iconScale, true);
        ctx.restore();

        // --- LIKES COUNT ---
        // From DOM: font-size 9px, font-weight 600, padding 0 8px
        const likesFontSize = 9 * scaleX;
        const likesY = iconY + iconSize + 4 * scaleY;  // 4px below icons
        ctx.fillStyle = '#ffffff';
        ctx.font = `600 ${Math.round(likesFontSize)}px -apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif`;
        ctx.textAlign = 'left';
        ctx.fillText('1,234 likes', iconStartX, likesY + likesFontSize);

        // --- CAPTION ---
        // From DOM: font-size 8px, padding 0 8px 6px
        const captionFontSize = 8 * scaleX;
        const captionY = likesY + likesFontSize + 4 * scaleY;
        ctx.font = `600 ${Math.round(captionFontSize)}px -apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif`;
        ctx.fillText('your_brand', iconStartX, captionY + captionFontSize);
        ctx.fillStyle = '#cccccc';
        ctx.font = `400 ${Math.round(captionFontSize)}px -apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif`;
        const captionTextX = iconStartX + ctx.measureText('your_brand ').width;
        ctx.fillText('キャプションを入力...', captionTextX, captionY + captionFontSize);

      } else if (platform === 'tiktok') {
        // TikTok style UI
        const sidebarX = screenX + screenW - 70;
        let iconY = screenY + screenH - 480;

        // Heart icon (filled for TikTok)
        ctx.save();
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        const hx = sidebarX, hy = iconY;
        const hs = 22;
        ctx.moveTo(hx, hy + hs * 0.3);
        ctx.bezierCurveTo(hx, hy - hs * 0.3, hx - hs, hy - hs * 0.5, hx - hs, hy + hs * 0.1);
        ctx.bezierCurveTo(hx - hs, hy + hs * 0.7, hx, hy + hs, hx, hy + hs * 1.3);
        ctx.bezierCurveTo(hx, hy + hs, hx + hs, hy + hs * 0.7, hx + hs, hy + hs * 0.1);
        ctx.bezierCurveTo(hx + hs, hy - hs * 0.5, hx, hy - hs * 0.3, hx, hy + hs * 0.3);
        ctx.fill();
        ctx.restore();

        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 26px -apple-system, BlinkMacSystemFont, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('24.5K', sidebarX, iconY + 55);

        // Comment icon
        iconY += 115;
        ctx.save();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 4;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        const cx = sidebarX, cy = iconY;
        const bw = 20, bh = 16, br = 5;
        ctx.moveTo(cx - bw + br, cy - bh);
        ctx.lineTo(cx + bw - br, cy - bh);
        ctx.arcTo(cx + bw, cy - bh, cx + bw, cy - bh + br, br);
        ctx.lineTo(cx + bw, cy + bh - br);
        ctx.arcTo(cx + bw, cy + bh, cx + bw - br, cy + bh, br);
        ctx.lineTo(cx - 4, cy + bh);
        ctx.lineTo(cx - 10, cy + bh + 12);
        ctx.lineTo(cx - 5, cy + bh);
        ctx.lineTo(cx - bw + br, cy + bh);
        ctx.arcTo(cx - bw, cy + bh, cx - bw, cy + bh - br, br);
        ctx.lineTo(cx - bw, cy - bh + br);
        ctx.arcTo(cx - bw, cy - bh, cx - bw + br, cy - bh, br);
        ctx.stroke();
        ctx.restore();

        ctx.fillStyle = '#ffffff';
        ctx.fillText('892', sidebarX, iconY + 52);

        // Share icon
        iconY += 110;
        ctx.save();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 4;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        const sx = sidebarX, sy = iconY;
        ctx.moveTo(sx - 14, sy + 14);
        ctx.lineTo(sx + 14, sy - 14);
        ctx.lineTo(sx + 14, sy + 6);
        ctx.moveTo(sx + 14, sy - 14);
        ctx.lineTo(sx - 6, sy - 14);
        ctx.stroke();
        ctx.restore();

        // Bottom username
        ctx.textAlign = 'left';
        const bottomY = screenY + screenH - 140;
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 32px -apple-system, BlinkMacSystemFont, sans-serif';
        ctx.fillText('@your_brand', screenX + 50, bottomY);
      }

      ctx.restore();

      // STEP 2: Draw the mockup frame image on top (transparent areas show content beneath)
      ctx.drawImage(frameImg, 0, 0);

      // Download the result
      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `mockup_${bannerIndex + 1}_${device}_${platform}.png`;
      link.href = dataUrl;
      link.click();

      // Restore button
      if (downloadBtn) {
        downloadBtn.innerHTML = originalText;
        downloadBtn.disabled = false;
      }

    } catch (error) {
      console.error('Mockup download failed:', error);
      alert(state.locale === 'ja' ? 'モックアップのダウンロードに失敗しました' : 'Failed to download mockup');

      // Restore button on error
      const downloadBtn = bannerRow?.querySelector('.banner-lab-v4__mockup-download');
      if (downloadBtn) {
        downloadBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7,10 12,15 17,10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> Mockup`;
        downloadBtn.disabled = false;
      }
    }
  }

  // ============================================
  // Bulk Download All
  // ============================================
  async function bulkDownloadAll() {
    const hasAnyImages = state.banners.some(banner =>
      banner.panels.some(p => p && p.image)
    );

    if (!hasAnyImages) {
      alert(state.locale === 'ja' ? '画像を追加してください' : 'Please add images first');
      return;
    }

    // Show progress
    const progressOverlay = document.createElement('div');
    progressOverlay.className = 'banner-lab-v4__bulk-progress';
    progressOverlay.innerHTML = `
      <div class="banner-lab-v4__bulk-progress-content">
        <div class="banner-lab-v4__bulk-progress-title">
          ${state.locale === 'ja' ? 'ダウンロード準備中...' : 'Preparing download...'}
        </div>
        <div class="banner-lab-v4__bulk-progress-bar">
          <div class="banner-lab-v4__bulk-progress-fill" style="width: 0%"></div>
        </div>
        <div class="banner-lab-v4__bulk-progress-text">0%</div>
      </div>
    `;
    document.body.appendChild(progressOverlay);

    const progressFill = progressOverlay.querySelector('.banner-lab-v4__bulk-progress-fill');
    const progressText = progressOverlay.querySelector('.banner-lab-v4__bulk-progress-text');

    try {
      const files = [];
      const totalItems = state.banners.length * 2;
      let completed = 0;

      for (let i = 0; i < state.banners.length; i++) {
        const banner = state.banners[i];
        const hasImages = banner.panels.some(p => p && p.image);

        if (hasImages) {
          // Generate banner
          const bannerBlob = await generateBannerBlob(i);
          if (bannerBlob) {
            const ext = banner.format === 'jpeg' ? 'jpg' : banner.format;
            files.push({
              name: `banner_${i + 1}_${banner.outputWidth}x${banner.outputHeight}.${ext}`,
              blob: bannerBlob
            });
          }
        }

        completed++;
        progressFill.style.width = (completed / totalItems * 100) + '%';
        progressText.textContent = Math.round(completed / totalItems * 100) + '%';
      }

      for (let i = 0; i < state.banners.length; i++) {
        const banner = state.banners[i];
        const hasImages = banner.panels.some(p => p && p.image);

        if (hasImages) {
          const mockupBlob = await generateMockupBlob(i);
          if (mockupBlob) {
            files.push({
              name: `mockup_${i + 1}_${banner.currentPlatform}.png`,
              blob: mockupBlob
            });
          }
        }

        completed++;
        progressFill.style.width = (completed / totalItems * 100) + '%';
        progressText.textContent = Math.round(completed / totalItems * 100) + '%';
      }

      // Download files
      progressText.textContent = state.locale === 'ja' ? 'ダウンロード中...' : 'Downloading...';

      for (let i = 0; i < files.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 300));
        const url = URL.createObjectURL(files[i].blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = files[i].name;
        link.click();
        URL.revokeObjectURL(url);
      }

      setTimeout(() => progressOverlay.remove(), 500);

    } catch (error) {
      console.error('Bulk download error:', error);
      progressOverlay.remove();
      alert(state.locale === 'ja' ? 'エラーが発生しました' : 'Error occurred');
    }
  }

  async function generateBannerBlob(bannerIndex) {
    const banner = state.banners[bannerIndex];
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = banner.outputWidth;
    canvas.height = banner.outputHeight;

    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    await Promise.all(banner.selectedLayout.panels.map((panelDef, index) => {
      return new Promise((resolve) => {
        const panelData = banner.panels[index];

        const px = panelDef.x * banner.outputWidth;
        const py = panelDef.y * banner.outputHeight;
        const pw = panelDef.w * banner.outputWidth;
        const ph = panelDef.h * banner.outputHeight;

        ctx.fillStyle = '#1a1a1a';
        ctx.fillRect(px, py, pw, ph);

        if (panelData && panelData.image) {
          if (panelData.isVideo && panelData.videoElement) {
            // Draw video frame for batch export
            ctx.save();
            ctx.beginPath();
            ctx.rect(px, py, pw, ph);
            ctx.clip();

            const imgWidth = panelData.naturalWidth * panelData.scale;
            const imgHeight = panelData.naturalHeight * panelData.scale;
            try {
              ctx.drawImage(panelData.videoElement, px + panelData.x, py + panelData.y, imgWidth, imgHeight);
            } catch (e) {
              console.warn('Could not draw video frame for batch export:', e);
            }
            ctx.restore();
            resolve();
          } else {
            // Draw image for batch export
            const img = new Image();
            img.onload = () => {
              ctx.save();
              ctx.beginPath();
              ctx.rect(px, py, pw, ph);
              ctx.clip();

              const imgWidth = panelData.naturalWidth * panelData.scale;
              const imgHeight = panelData.naturalHeight * panelData.scale;
              ctx.drawImage(img, px + panelData.x, py + panelData.y, imgWidth, imgHeight);

              ctx.restore();
              resolve();
            };
            img.onerror = resolve;
            img.src = panelData.image;
          }
        } else {
          resolve();
        }
      });
    }));

    return new Promise((resolve) => {
      let mimeType, quality;
      switch (banner.format) {
        case 'webp':
          if (state.supportsWebP) {
            mimeType = 'image/webp'; quality = 0.92;
          } else {
            mimeType = 'image/png'; quality = undefined;
          }
          break;
        case 'jpeg': mimeType = 'image/jpeg'; quality = 0.92; break;
        default: mimeType = 'image/png'; quality = undefined;
      }
      canvas.toBlob(resolve, mimeType, quality);
    });
  }

  async function generateMockupBlob(bannerIndex) {
    // Find the phone frame element for this banner
    const bannerRow = document.querySelector(`.banner-lab-v4__banner-row[data-banner-index="${bannerIndex}"]`);
    if (!bannerRow) {
      console.error('Banner row not found for generateMockupBlob');
      return null;
    }

    const phoneFrame = bannerRow.querySelector('.banner-lab-v4__phone-frame');
    if (!phoneFrame) {
      console.error('Phone frame not found for generateMockupBlob');
      return null;
    }

    // Load html2canvas if not already loaded
    if (typeof html2canvas === 'undefined') {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }

    try {
      // CRITICAL: Create isolated container to prevent other page elements from bleeding into capture
      // IMPORTANT: Render off-screen to avoid visible flash during capture
      const isolatedContainer = document.createElement('div');
      isolatedContainer.id = 'mockup-blob-capture-container';
      isolatedContainer.style.cssText = `
        position: fixed !important;
        top: 0 !important;
        left: -9999px !important;
        width: 100vw !important;
        height: 100vh !important;
        z-index: 2147483647 !important;
        background: white !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        pointer-events: none !important;
        overflow: hidden !important;
      `;

      // Deep clone the phone frame
      const phoneFrameClone = phoneFrame.cloneNode(true);

      // Copy computed styles to ensure accurate rendering
      const computedStyle = window.getComputedStyle(phoneFrame);
      phoneFrameClone.style.width = computedStyle.width;
      phoneFrameClone.style.height = computedStyle.height;
      phoneFrameClone.style.position = 'relative';
      phoneFrameClone.style.transform = 'none';
      phoneFrameClone.style.margin = '0';
      phoneFrameClone.style.overflow = 'hidden';

      // CRITICAL: Remove placeholder and dropzone elements that might bleed through
      const elementsToRemove = phoneFrameClone.querySelectorAll('.mockup-placeholder, [class*="dropzone"], [class*="drop-zone"], [class*="placeholder"]');
      elementsToRemove.forEach(el => el.remove());

      // Handle images in clone - copy current src
      const originalImages = phoneFrame.querySelectorAll('img');
      const clonedImages = phoneFrameClone.querySelectorAll('img');
      originalImages.forEach((img, i) => {
        if (clonedImages[i] && img.src) {
          clonedImages[i].src = img.src;
        }
      });

      // Handle videos - convert to canvas snapshots before cloning
      const originalVideos = phoneFrame.querySelectorAll('video');
      const clonedVideos = phoneFrameClone.querySelectorAll('video');
      originalVideos.forEach((video, i) => {
        if (clonedVideos[i] && video.videoWidth > 0) {
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = video.videoWidth;
          tempCanvas.height = video.videoHeight;
          const ctx = tempCanvas.getContext('2d');
          try {
            ctx.drawImage(video, 0, 0);
            const img = document.createElement('img');
            img.src = tempCanvas.toDataURL('image/png');
            img.style.cssText = clonedVideos[i].style.cssText;
            img.style.width = '100%';
            img.style.height = '100%';
            img.style.objectFit = 'cover';
            clonedVideos[i].parentNode.replaceChild(img, clonedVideos[i]);
          } catch (e) {
            console.warn('Could not capture video frame:', e);
          }
        }
      });

      // CRITICAL: Convert canvas elements to img elements (html2canvas doesn't capture canvas content properly)
      const originalCanvases = phoneFrame.querySelectorAll('canvas');
      const clonedCanvases = phoneFrameClone.querySelectorAll('canvas');
      originalCanvases.forEach((origCanvas, i) => {
        if (clonedCanvases[i] && origCanvas.width > 0 && origCanvas.height > 0) {
          try {
            // Convert canvas to data URL and create img element
            const dataUrl = origCanvas.toDataURL('image/png');
            const img = document.createElement('img');
            img.src = dataUrl;
            // Copy the canvas's computed styles to preserve layout
            const canvasStyle = window.getComputedStyle(origCanvas);
            img.style.width = canvasStyle.width;
            img.style.height = canvasStyle.height;
            img.style.display = canvasStyle.display;
            img.style.position = canvasStyle.position;
            img.style.objectFit = 'cover';
            // Replace canvas with img
            clonedCanvases[i].parentNode.replaceChild(img, clonedCanvases[i]);
          } catch (e) {
            console.warn('Could not convert canvas to image:', e);
          }
        }
      });

      // CRITICAL: Get phone frame dimensions and device type
      const frameWidth = parseFloat(window.getComputedStyle(phoneFrame).width);
      const frameHeight = parseFloat(window.getComputedStyle(phoneFrame).height);
      const device = state.banners[bannerIndex]?.currentDevice || 'apple';

      // Device-specific border radius values (matching CSS)
      const deviceRadiusMap = {
        apple: { outer: 44, inner: 36 },
        google: { outer: 40, inner: 30 },
        samsung: { outer: 12, inner: 6 },
        huawei: { outer: 40, inner: 34 },
        generic: { outer: 16, inner: 12 }
      };
      const radiusConfig = deviceRadiusMap[device] || deviceRadiusMap.apple;
      const outerRadius = radiusConfig.outer;
      const innerRadius = radiusConfig.inner;

      // HIGH-RES OUTPUT: Scale up clone for better quality
      // Use exact aspect ratio from frame - no correction needed
      const targetWidth = 540;
      const targetHeight = Math.round(targetWidth * (frameHeight / frameWidth));
      const scaleFactor = targetWidth / frameWidth;

      // Apply dimensions to clone
      phoneFrameClone.style.width = targetWidth + 'px';
      phoneFrameClone.style.height = targetHeight + 'px';

      // Ensure overflow is hidden on phone-screen
      const phoneScreenClone = phoneFrameClone.querySelector('.phone-screen');
      if (phoneScreenClone) {
        phoneScreenClone.style.overflow = 'hidden';
        phoneScreenClone.style.clipPath = `inset(0 round ${innerRadius * scaleFactor}px)`;
        // Force all images to fit at full quality
        const images = phoneScreenClone.querySelectorAll('img');
        images.forEach(img => {
          img.style.width = '100%';
          img.style.height = '100%';
          img.style.objectFit = 'cover';
          img.style.imageRendering = 'high-quality';
        });
      }

      isolatedContainer.appendChild(phoneFrameClone);
      document.body.appendChild(isolatedContainer);

      // Wait for images to load and render
      await new Promise(resolve => setTimeout(resolve, 800));

      // Use scale 4 for high quality output
      const captureScale = 4;

      // Capture at enlarged dimensions
      const rawCanvas = await html2canvas(phoneFrameClone, {
        scale: captureScale,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        logging: false,
        width: targetWidth,
        height: targetHeight,
        windowWidth: targetWidth,
        windowHeight: targetHeight,
        imageTimeout: 0,
        removeContainer: false
      });

      // CRITICAL: Create canvas with proper clipping
      const canvas = document.createElement('canvas');
      canvas.width = rawCanvas.width;
      canvas.height = rawCanvas.height;
      const ctx = canvas.getContext('2d');

      // Calculate scaled radius for clipping
      const scaledOuterRadius = outerRadius * scaleFactor * captureScale;

      // Apply rounded corner clip path FIRST
      ctx.beginPath();
      const w = canvas.width, h = canvas.height;
      ctx.moveTo(scaledOuterRadius, 0);
      ctx.lineTo(w - scaledOuterRadius, 0);
      ctx.quadraticCurveTo(w, 0, w, scaledOuterRadius);
      ctx.lineTo(w, h - scaledOuterRadius);
      ctx.quadraticCurveTo(w, h, w - scaledOuterRadius, h);
      ctx.lineTo(scaledOuterRadius, h);
      ctx.quadraticCurveTo(0, h, 0, h - scaledOuterRadius);
      ctx.lineTo(0, scaledOuterRadius);
      ctx.quadraticCurveTo(0, 0, scaledOuterRadius, 0);
      ctx.closePath();
      ctx.clip();

      // Draw the captured content within clipped area
      ctx.drawImage(rawCanvas, 0, 0);

      // CRITICAL: Remove isolated container after capture
      if (isolatedContainer && isolatedContainer.parentNode) {
        isolatedContainer.parentNode.removeChild(isolatedContainer);
      }

      return new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/png');
      });

    } catch (error) {
      console.error('generateMockupBlob failed:', error);
      // Clean up isolated container on error
      const container = document.getElementById('mockup-blob-capture-container');
      if (container) container.remove();
      return null;
    }
  }

  // ============================================
  // Start
  // ============================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
